import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { Form, Input, Button, Row, Col, Typography, Flex, Radio, Switch, Slider, Upload, InputNumber, Collapse, Select } from 'antd';
import ImgCrop from 'antd-img-crop';
import { appConfigAtomState } from '../../../state/reducerAtoms/app.atom';
import { translateThis } from '../../../helpers/language.helper';
import { showSnackbar } from '../../../components/snackbar/index';
import SVGIcons from '../../../assets/images/icons/svgIcons';
import ApiCaller from '../../../services/api/general';
import {
    getGmailAuthUrl,
    getGmailStatus,
    getGmailConnectionStatus,
    configureGmailForModule,
    syncGmailForModule,
    disconnectGmailForModule,
} from '../../../services/api/gmail';
import './index.scss';
import { getCurrentFacilityURL } from '../../../helpers/helper.functions';
import AppIcons from '../../../assets/images/icons';
import S3DocumentUploader from '../../../components/awsS3DocumentUploader/document.uploader';
import { uploadLogo, getFileByKey } from '../../../services/api/aws';
import PageLayout from '../../app';
import AvatarViewer from '../../../components/iconPicker/avatarViewer';
import MultipleFileUpload from '../../../components/multipleFileUpload';
import LogoPreview from '../../../components/logoPreview/logoPreview';
import ImageCropUploader from '../../../components/uploaderWithCrop/imageCropUploader';



const { Title, Paragraph } = Typography;

export default function AIAddEditModule() {
    const appState = useRecoilValue(appConfigAtomState);

    const navigate = useNavigate();
    const { id } = useParams();
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadedDocuments, setUploadedDocuments] = useState([]);
    const [uploadedLogo, setUploadedLogo] = useState('');
    const [question, setQuestion] = useState('');
    const [answer, setAnswer] = useState('');
    const [questionAnswers, setQuestionAnswers] = useState([]);
    const [avatarIcon, setAvatarIcon] = useState('');
    const [widgetMode, setWidgetMode] = useState('bubble');

    const [formData, setFormData] = useState({
        name: '',
        title: '',
        description: '',
        role: '',
        mode: '',
        widgetMode,
        modelVersion: 'gpt-4.1',
        agentName: '',
        personality: '',
        defaultLanguage: '',
        useCase: '',
        dynamicPrompt: '',
        dynamicPromptStatus: false,
        isHeaderPresent: false,
        isAddRotatingAvatarAndLogo: false,
        training: {},
        parameters: {},
        createdCode: '',
        avatarImg: avatarIcon,
        conversationBoxTitle: '',
        themeBackgroundColor: '',
        customChatHeader: '',
        chatLogoImg: '',
        selectPreview: 'desktop',
        agentType: 'customer_support',
        salesConfig: {
            enabledFields: { interestIn: true, goal: true, timelineUrgency: true },
            qualificationQuestions: [],
            relevanceCheck: { enabled: false, rules: '', irrelevantMessage: '' },
            stagePrompts: { greet: '', relevanceCheck: '', qualify: '', persuade: '', convert: '' },
            conversionGoal: ''
        }
    });

    // Gmail integration (per-module): status, loading, and config
    const [searchParams, setSearchParams] = useSearchParams();
    const [gmailStatus, setGmailStatus] = useState(null);
    const [gmailLoading, setGmailLoading] = useState(false);
    const [gmailSyncing, setGmailSyncing] = useState(false);
    const [gmailConnecting, setGmailConnecting] = useState(false);
    const [gmailConfig, setGmailConfig] = useState({ enabled: false, syncFrequency: 'manual' });
    const [gmailDisconnecting, setGmailDisconnecting] = useState(false);

    // Normalize backend response: Option A (connection-status) uses connected/email, Option B (status) uses gmailConnected/connectedEmail
    const normalizeGmailConnection = (data) => {
        if (!data) return null;
        const connected = data.connected ?? data.gmailConnected;
        const email = data.email ?? data.connectedEmail ?? null;
        return { ...data, connected: connected === true, email: email && String(email).trim() ? String(email) : null };
    };


    const handleLogoUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        uploadLogo(file, (result) => {
            if (result.success) {
                setFormValue('chatLogoImg', result.imageKey);
                setUploadedLogo({ file, preview: result.preview });
            }
        });
    };

    useEffect(() => {
        if (id) {
            getData(id);
        }

    }, [id]);

    // Fetch Gmail connection state from backend (supports Option A: connection-status, Option B: status)
    const fetchGmailConnectionState = async (moduleId) => {
        if (!moduleId) return;
        setGmailLoading(true);
        let result = await getGmailConnectionStatus(moduleId);
        if (!result.success || result.data == null) {
            result = await getGmailStatus(moduleId);
        }
        setGmailLoading(false);
        if (result.success && result.data != null) {
            const normalized = normalizeGmailConnection(result.data);
            setGmailStatus(normalized);
            const d = result.data;
            if (d.enabled !== undefined) setGmailConfig((c) => ({ ...c, enabled: d.enabled }));
            if (d.syncFrequency) setGmailConfig((c) => ({ ...c, syncFrequency: d.syncFrequency }));
            if (d.gmailIntegration?.enabled !== undefined) setGmailConfig((c) => ({ ...c, enabled: d.gmailIntegration.enabled }));
            if (d.gmailIntegration?.syncFrequency) setGmailConfig((c) => ({ ...c, syncFrequency: d.gmailIntegration.syncFrequency }));
        } else {
            setGmailStatus(null);
        }
    };

    useEffect(() => {
        if (id) fetchGmailConnectionState(id);
    }, [id]);

    // Handle OAuth success redirect: ?gmail=connected – always refetch from backend so UI shows "Gmail connected on {email}"
    useEffect(() => {
        const gmailParam = searchParams.get('gmail');
        if (id && gmailParam === 'connected') {
            showSnackbar().success(translateThis('Gmail connected successfully'));
            const next = new URLSearchParams(searchParams);
            next.delete('gmail');
            setSearchParams(next, { replace: true });
            fetchGmailConnectionState(id);
        }
    }, [id, searchParams]);

    const handleConnectGmail = async () => {
        if (!id || gmailConnecting) return;
        setGmailConnecting(true);
        const result = await getGmailAuthUrl(id);
        setGmailConnecting(false);
        if (result.success && result.data?.authUrl) {
            window.location.href = result.data.authUrl;
        } else {
            showSnackbar().failure(result.message || translateThis('Failed to get Gmail authorization URL'));
        }
    };

    const handleGmailSyncNow = async () => {
        if (!id || gmailSyncing) return;
        setGmailSyncing(true);
        const result = await syncGmailForModule(id);
        setGmailSyncing(false);
        if (result.success) {
            showSnackbar().success(translateThis('Sync started'));
            fetchGmailConnectionState(id);
        } else {
            showSnackbar().failure(result.message || translateThis('Sync failed'));
        }
    };

    const handleGmailConfigChange = async (updates) => {
        if (!id) return;
        const next = { ...gmailConfig, ...updates };
        setGmailConfig(next);
        const result = await configureGmailForModule(id, {
            enabled: next.enabled,
            syncFrequency: next.syncFrequency,
        });
        if (!result.success) {
            setGmailConfig(gmailConfig);
            showSnackbar().failure(result.message || translateThis('Failed to save Gmail settings'));
        }
    };

    const handleGmailDisconnect = async () => {
        if (!id || gmailDisconnecting) return;
        setGmailDisconnecting(true);
        const result = await disconnectGmailForModule(id);
        setGmailDisconnecting(false);
        if (result.success) {
            showSnackbar().success(translateThis('Gmail disconnected'));
            setGmailStatus(null);
            setGmailConfig({ enabled: false, syncFrequency: 'manual' });
        } else {
            showSnackbar().failure(result.message || translateThis('Failed to disconnect Gmail'));
        }
    };

    // get data from api
    const getData = (id) => {
        setIsLoading(true);
        const apiCaller = new ApiCaller('modules');
        apiCaller.getData(id)
            .then(({ data }) => {
                const formData = {
                    name: data?.name ?? '',
                    title: data?.title ?? '',
                    description: data?.description ?? '',
                    mode: data?.mode ?? '',
                    widgetMode: data?.widgetMode ?? '',
                    modelVersion: data?.modelVersion ?? 'gpt-4.1',
                    defaultLanguage: data?.defaultLanguage ?? '',
                    role: data?.role?.name ?? 'university',
                    useCase: data?.useCase ?? '',
                    personality: data?.personality,
                    training: data?.training ?? {},
                    dynamicPrompt: data?.dynamicPrompt ?? '',
                    agentName: data?.agentName ?? '',
                    dynamicPromptStatus: data?.dynamicPromptStatus ?? false,
                    isHeaderPresent: data?.isHeaderPresent ?? false,
                    isAddRotatingAvatarAndLogo: data?.isAddRotatingAvatarAndLogo ?? false,
                    parameters: data?.parameters ?? {},
                    conversationBoxTitle: data?.conversationBoxTitle ?? '',
                    createdCode: data?.createdCode ?? '',
                    avatarImg: data?.avatarImg ?? '',
                    chatHeaderImg: data?.chatHeaderImg ?? '',
                    themeBackgroundColor: data?.themeBackgroundColor ?? '',
                    customChatHeader: data?.customChatHeader ?? '',
                    chatLogoImg: data?.chatLogoImg ?? '',
                    selectPreview: data?.selectPreview ?? 'desktop',
                    agentType: data?.agentType ?? 'customer_support',
                    salesConfig: {
                        enabledFields: data?.salesConfig?.enabledFields ?? { interestIn: true, goal: true, timelineUrgency: true },
                        qualificationQuestions: data?.salesConfig?.qualificationQuestions ?? [],
                        relevanceCheck: data?.salesConfig?.relevanceCheck ?? { enabled: false, rules: '', irrelevantMessage: '' },
                        stagePrompts: data?.salesConfig?.stagePrompts ?? { greet: '', relevanceCheck: '', qualify: '', persuade: '', convert: '' },
                        conversionGoal: data?.salesConfig?.conversionGoal ?? ''
                    }
                }
                setFormData(formData);
                setWidgetMode(data?.widgetMode || 'bubble');
                if (data?.chatLogoImg && !data.chatLogoImg.startsWith('blob:')) {
                    setUploadedLogo({ preview: data.chatLogoImg });
                } else {
                    setUploadedLogo('');
                }
                setUploadedDocuments(data?.documents || []);
                setQuestionAnswers(formData?.training?.examples || []);
                setSelectedAvatar(formData?.avatarImg || '');
                setSelectedHeader(formData?.chatHeaderImg || '');
            })
            .catch(() => showSnackbar().failure(`${translateThis('Something went wrong')}`))
            .finally(() => setIsLoading(false));
    }

    /**
   * sets the form data
   * @param {string} name the name of the field
   * @param {string} value  the value of the field
   */
    const setFormValue = (name, value) => {
        setFormData((prv) => ({ ...prv, [name]: value }))

    }

    // ---- End sales helpers ----

    /**
     * Validates the form and returns true if valid
     * @returns boolean
     */
    const isValidForm = () => {
        if (!formData.name || !formData.role || !formData.useCase) {
            return false;
        }

        return true;
    }

    /**
     * On submit the form
     */
    const onSubmit = () => {
        if (isValidForm()) {
            // Submitting data to backend for registration
            setIsSubmitting(true);
            const functionToCall = id ? updateData : postData;
            functionToCall().then((data) => {
                if (data.success) {
                    navigate(`/${getCurrentFacilityURL()}/ai-development/modules`, { replace: true });
                    showSnackbar().success(data.message)
                } else {
                    showSnackbar().failure(data.message)

                }
            }).finally(() => setIsSubmitting(false));

        }

    };


    /**
     * To save a module
     */
    const postData = async () => {
        const apiCaller = new ApiCaller('modules');
        const postInput = { ...formData, widgetMode, training: { ...formData.training, examples: questionAnswers } };
        return apiCaller.postData(postInput);
    }

    /**
     * To update a module
     */
    const updateData = async () => {
        const apiCaller = new ApiCaller('modules');
        const postInput = { ...formData, widgetMode, training: { ...formData.training, examples: questionAnswers, dynamicPromptStatus: formData?.dynamicPromptStatus, dynamicPrompt: formData?.dynamicPrompt } };
        return apiCaller.customPutData(id, postInput);
    }

    /**
         * To delete a document
         */
    const deleteDocument = async (docTitle) => {
        const apiCaller = new ApiCaller('ai');
        apiCaller.customPostData(`/modules/${id}/delete-document`, { title: docTitle, moduleId: id }).then(({ data }) => {
            if (data?.documents) {
                setUploadedDocuments(data?.documents)
            }
        })
    }

    // get data from api
    const refreshDocuments = () => {
        const apiCaller = new ApiCaller('modules');
        apiCaller.getData(id)
            .then(({ data }) => {
                setUploadedDocuments(data?.documents || []);
            });
    }


    const marks = {
        0: (
            <>
                0<br />
                <i>{translateThis('Simple')}</i>
            </>
        ),
        0.5: '0.5',
        1: '1',
        1.5: '1.5',
        2: (
            <>
                2<br />
                <i>{translateThis('Creative')}</i>
            </>
        ),
    };

    console.log(formData, 'formData');

    /**
     * When adding a question and answer
     */
    const onQnAnAdd = () => {
        if (question && answer) {
            setQuestion('');
            setAnswer('');
            setQuestionAnswers((prv) => ([...prv, { question, answer }]));
        }

    }
    /**
     * When adding a question and answer
     * @param {string} i
     */
    const onQnAnRemove = (i) => {
        const qnAnArr = questionAnswers;
        qnAnArr.splice(i, 1);
        setQuestionAnswers([...qnAnArr]);

    }

    const moduleEmbeddedURL = `${process.env.REACT_APP_AI_APP_URL}/${getCurrentFacilityURL()}/chat/${formData.name}`;
    const copyToClipboard = async (text = '') => {
        if (!text) return false;
        try {
            if (navigator.clipboard?.writeText && window.isSecureContext) {
                await navigator.clipboard.writeText(text);
                return true;
            }
        } catch (e) {
            // fall back below
        }

        // Fallback for non-secure contexts (http) or blocked permissions
        try {
            const el = document.createElement('textarea');
            el.value = text;
            el.setAttribute('readonly', '');
            el.style.position = 'fixed';
            el.style.left = '-9999px';
            el.style.top = '0';
            document.body.appendChild(el);
            el.focus();
            el.select();
            const ok = document.execCommand('copy');
            document.body.removeChild(el);
            return ok;
        } catch (e) {
            return false;
        }
    };

    const copyEmbeddingURL = async () => {
        const ok = await copyToClipboard(moduleEmbeddedURL);
        if (ok) showSnackbar().info(`${translateThis('URL Copied to clipboard')}`);
        else showSnackbar().warning(`${translateThis('Unable to copy. Please copy manually.')}`);
    }

    const buildEmbedCode = React.useCallback((name, mode) => {
        if (!name) return '';
        const facilityURL = getCurrentFacilityURL();
        const baseAppURL = process.env.REACT_APP_AI_APP_URL;
        const embedURL = mode === 'window'
            ? `${baseAppURL}/${facilityURL}/chat/${name}`
            : `${baseAppURL}/${facilityURL}/${name}/chat`;
        const widgetSrc = `${window.location.origin}/pnp-ai-widget.js`;
        const lines = [
            '<!-- PnP AI Agent Widget -->',
            `<script`,
            `  src="${widgetSrc}"`,
            `  data-url="${embedURL}"`,
            `  data-mode="${mode}"`,
        ];
        if (mode === 'window') {
            lines.push(`  data-target-id="${name}"`);
        }
        if (mode === 'bubble') {
            lines.push(`  data-bubble-width="75"`);
            lines.push(`  data-bubble-height="75"`);
            lines.push(`  data-bubble-position="rightBottom"`);
            lines.push(`  data-bubble-offset='{"bottom":18,"right":18}'`);
        }
        lines.push(`></script>`);
        return lines.join('\n');
    }, []);

    React.useEffect(() => {
        const code = buildEmbedCode(formData.name, widgetMode);
        if (code) setFormValue('createdCode', code);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.name, widgetMode]);

    const copyEmbedCodeSnippet = async () => {
        const ok = await copyToClipboard(formData.createdCode || '');
        if (ok) showSnackbar().info(`${translateThis('Code Copied to clipboard')}`);
        else showSnackbar().warning(`${translateThis('Unable to copy. Please copy manually.')}`);
    };

    const avatarIconList = [
        {
            iconName: AppIcons.avatar1,
            fileKey: 'avatar1',
        },
        {
            iconName: AppIcons.avatar2,
            fileKey: 'avatar2',
        },
        {
            iconName: AppIcons.avatar3,
            fileKey: 'avatar3',
        },
        {
            iconName: AppIcons.avatar4,
            fileKey: 'avatar4',
        },
        {
            iconName: AppIcons.avatar5,
            fileKey: 'avatar5',
        },
        {
            iconName: AppIcons.avatar6,
            fileKey: 'avatar6',
        },
        {
            iconName: AppIcons.avatar7,
            fileKey: 'avatar7',
        },
        {
            iconName: AppIcons.avatar8,
            fileKey: 'avatar8',
        },
        {
            iconName: AppIcons.avatar9,
            fileKey: 'avatar9',
        },
        {
            iconName: AppIcons.avatar10,
            fileKey: 'avatar10',
        },
        {
            iconName: AppIcons.avatar11,
            fileKey: 'avatar11',
        },
        {
            iconName: AppIcons.avatar12,
            fileKey: 'avatar12',
        },
        {
            iconName: AppIcons.avatar13,
            fileKey: 'avatar13',
        },
        {
            iconName: AppIcons.avatar14,
            fileKey: 'avatar14',
        },
        {
            iconName: AppIcons.avatar15,
            fileKey: 'avatar15',
        },
        {
            iconName: AppIcons.avatar16,
            fileKey: 'avatar16',
        },
        {
            iconName: AppIcons.avatar17,
            fileKey: 'avatar17',
        },
    ];

    const humanLookAvatar = [
        {
            iconName: AppIcons.hAvatar1,
            fileKey: 'hAvatar1',
        },
        {
            iconName: AppIcons.hAvatar2,
            fileKey: 'hAvatar2',
        },
        {
            iconName: AppIcons.hAvatar3,
            fileKey: 'hAvatar3',
        },
        {
            iconName: AppIcons.hAvatar4,
            fileKey: 'hAvatar4',
        },
        {
            iconName: AppIcons.hAvatar5,
            fileKey: 'hAvatar5',
        },
        {
            iconName: AppIcons.hAvatar6,
            fileKey: 'hAvatar6',
        },
        {
            iconName: AppIcons.hAvatar7,
            fileKey: 'hAvatar7',
        },
        {
            iconName: AppIcons.hAvatar8,
            fileKey: 'hAvatar8',
        },
        {
            iconName: AppIcons.hAvatar9,
            fileKey: 'hAvatar9',
        },
        {
            iconName: AppIcons.hAvatar10,
            fileKey: 'hAvatar10',
        },

    ];

    const botLookAvatar = [
        {
            iconName: AppIcons.bAvatar1,
            fileKey: 'bAvatar1',
        },
        {
            iconName: AppIcons.bAvatar2,
            fileKey: 'bAvatar2',
        },
        {
            iconName: AppIcons.bAvatar3,
            fileKey: 'bAvatar3',
        },
        {
            iconName: AppIcons.bAvatar4,
            fileKey: 'bAvatar4',
        },
        {
            iconName: AppIcons.bAvatar5,
            fileKey: 'bAvatar5',
        },
        {
            iconName: AppIcons.bAvatar6,
            fileKey: 'bAvatar6',
        },
        {
            iconName: AppIcons.bAvatar7,
            fileKey: 'bAvatar7',
        },
        {
            iconName: AppIcons.bAvatar8,
            fileKey: 'bAvatar8',
        },
        {
            iconName: AppIcons.bAvatar9,
            fileKey: 'bAvatar9',
        },
        {
            iconName: AppIcons.bAvatar10,
            fileKey: 'bAvatar10',
        },
        {
            iconName: AppIcons.bAvatar11,
            fileKey: 'bAvatar11',
        },
        {
            iconName: AppIcons.bAvatar12,
            fileKey: 'bAvatar12',
        },
        {
            iconName: AppIcons.bAvatar13,
            fileKey: 'bAvatar13',
        },
        {
            iconName: AppIcons.bAvatar14,
            fileKey: 'bAvatar14',
        },
        {
            iconName: AppIcons.bAvatar15,
            fileKey: 'bAvatar15',
        },
        {
            iconName: AppIcons.bAvatar16,
            fileKey: 'bAvatar16',
        },
        {
            iconName: AppIcons.bAvatar17,
            fileKey: 'bAvatar17',
        },
        {
            iconName: AppIcons.bAvatar18,
            fileKey: 'bAvatar18',
        },
        {
            iconName: AppIcons.bAvatar19,
            fileKey: 'bAvatar19',
        },

    ];


    const [selectedAvatar, setSelectedAvatar] = useState(null); // This can be a unique ID or key

    const avatarSelect = (element) => {
        if (selectedAvatar === element) {
            setSelectedAvatar(null); // Deselect on second click
        } else {
            setSelectedAvatar(element);
        }
        setAvatarIcon(element.fileKey);
    };

    const [selectedHeader, setSelectedHeader] = useState(formData?.chatHeaderImg || 'header1');

    // Optional: Update preview if formData changes externally
    useEffect(() => {
        if (formData?.chatHeaderImg) {
            setSelectedHeader(formData.chatHeaderImg);
        }
        if (formData?.avatarImg) {
            setAvatarIcon(formData.avatarImg);
        }
    }, [formData]);

    const handleHeaderChange = (e) => {
        const value = e.target.value;
        setSelectedHeader(value);
        setFormValue('chatHeaderImg', value);
    };


    const headerImages = {
        header1: AppIcons.chatHead1,
        header2: AppIcons.chatHead2,
        header3: AppIcons.chatHead3,
        header4: AppIcons.chatHead4,
        header5: AppIcons.chatHead5,
        header6: AppIcons.chatHead6,
        header7: AppIcons.chatHead7,
    };

    const mergedHeaders = {
        ...headerImages,
        customBGHeader: formData.customChatHeader,
    };

    console.log(mergedHeaders, 'mergedHeaders');

    const scrollToTop = useRef(null);
    const [currentFormStep, setCurrentFormStep] = useState(0);
    const totalSteps = 4;

    const getSectionClass = (index) => {
        if (index < currentFormStep) return 'completed';
        if (index === currentFormStep) return 'progress';
        return 'upcoming';
    };

    const [fileList, setFileList] = useState([]);
    const onChange = ({ fileList: newFileList }) => {
        setFileList(newFileList);
    };
    const onPreview = async (file) => {
        let src = file.url;
        if (!src) {
            src = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.readAsDataURL(file.originFileObj);
                reader.onload = () => resolve(reader.result);
            });
        }
        const image = new Image();
        image.src = src;
        const imgWindow = window.open(src);
        imgWindow?.document.write(image.outerHTML);
    };


    return (

        <PageLayout
            pageName={'AI Agent'}
            pageTitle={'AI Agent'}
            bodyClassName={`module-main-wrp`}
            hideHeaderOnScroll
            key={`${appState?.selectedLang}`}
        >


            <div className='main-content-wrapper AI-module-view f-wrp' ref={scrollToTop}>

                <div className='appForm-layout'>
                    <br />
                    {!isLoading ? <Form
                        name="moduleView"
                        // disabled={!isEditEnabled}
                        initialValues={formData}
                        // onFinish={onFinish}
                        // onFinishFailed={onFinishFailed}
                        layout="vertical"
                    >
                        <div className="container">
                            <Flex gap={0} justify='space-between' vertical="true">
                                <Flex horizontal="true" className='sticky-stepper'>
                                    <div className='stepper-box f-wrp'>
                                        <button type='button' onClick={() => { setCurrentFormStep(0); scrollToTop.current?.scrollIntoView({ behavior: 'smooth' }); }} className={`each-step-items ${getSectionClass(0)}`}>
                                            <p>{translateThis('Create')}</p>
                                        </button>
                                        <button type='button' onClick={() => { setCurrentFormStep(1); scrollToTop.current?.scrollIntoView({ behavior: 'smooth' }); }} className={`each-step-items ${getSectionClass(1)}`}>
                                            <p>{translateThis('Train')}</p>
                                        </button>
                                        <button type='button' onClick={() => { setCurrentFormStep(2); scrollToTop.current?.scrollIntoView({ behavior: 'smooth' }); }} className={`each-step-items ${getSectionClass(2)}`}>
                                            <p>{translateThis('Design')}</p>
                                        </button>
                                        <button type='button' onClick={() => { setCurrentFormStep(3); scrollToTop.current?.scrollIntoView({ behavior: 'smooth' }); }} className={`each-step-items ${getSectionClass(3)}`}>
                                            <p>{translateThis('Deploy')}</p>
                                        </button>
                                    </div>
                                </Flex>

                                {/* -------------------------------------- start of page one ----------------------------------------- */}
                                {currentFormStep === 0 ?
                                    <Row gutter={90}>
                                        <Col span={24}><br /> <br /></Col>
                                        <Col span={12} xs={24} sm={24} md={12} lg={12} xl={12}>
                                            <Title level={4} style={{ margin: '0' }}>{translateThis('Module title')} 🏷️</Title>
                                            <Paragraph>{translateThis('Choose who your AI becomes this role guides how it interacts with users.')}</Paragraph>
                                            <Form.Item
                                                label="Module title"
                                                name="title"
                                                rules={[{ required: true, message: `${translateThis('Please enter Module title here')}` }]}
                                                style={{ marginBottom: '52px' }}
                                            >
                                                <Input
                                                    value={formData.title}
                                                    onChange={(e) => setFormValue('title', e.target.value)}
                                                    placeholder={translateThis('Type here')} />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12} xs={24} sm={24} md={12} lg={12} xl={12}>
                                            <Title level={4} style={{ margin: '0' }}>*{translateThis('For use of ...')}</Title>
                                            <Paragraph>&nbsp;</Paragraph>
                                            <br />
                                            <Form.Item
                                                name="role"
                                                initialValue={formData?.role}
                                                style={{ marginBottom: '52px' }}
                                                required
                                            >

                                                <Radio.Group
                                                    onChange={(e) => setFormValue('role', e.target.value)}
                                                >
                                                    <Radio value="university">{translateThis("Internal (Only your team can access)")}</Radio>
                                                    <Radio value="student">{translateThis("Public (For use of your clients /audience )")}</Radio>
                                                </Radio.Group>
                                            </Form.Item>

                                        </Col>

                                        {/* -------- Agent Type Selector -------- */}
                                        <Col span={24}>
                                            <Title level={4} style={{ margin: '0' }}>{translateThis('Agent Type')} 🎯</Title>
                                            <Paragraph>{translateThis('Choose the type of agent. This determines ticket behavior automatically.')}</Paragraph>
                                            <Form.Item
                                                name="agentType"
                                                initialValue={formData?.agentType}
                                                style={{ marginBottom: '52px' }}
                                            >
                                                <Radio.Group
                                                    value={formData.agentType}
                                                    onChange={(e) => setFormValue('agentType', e.target.value)}
                                                >
                                                    <Radio value="customer_support">{translateThis('Customer Support')}</Radio>
                                                    <Radio value="sales">{translateThis('Sales')}</Radio>
                                                </Radio.Group>
                                            </Form.Item>
                                        </Col>


                                        <Col span={24}>
                                            <Title level={4} style={{ margin: '0' }}>{translateThis('Description')} 📌</Title>
                                            <Paragraph>{translateThis('Briefly describe the purpose of this assistant and what it’s meant to help with.')}</Paragraph>
                                            <Form.Item
                                                label="Description"
                                                name="description"
                                                style={{ marginBottom: '52px' }}
                                            >
                                                <Input.TextArea
                                                    value={formData.description}
                                                    onChange={(e) => setFormValue('description', e.target.value)}
                                                    autoSize={{ minRows: 4, maxRows: 7 }}
                                                    style={{ resize: 'vertical' }}
                                                    placeholder={translateThis('Type here')} />
                                            </Form.Item>
                                        </Col>
                                        <Col span={24}>



                                            <Row gutter={24} style={{ marginTop: 32 }}>

                                                <Col xs={24} md={12}>
                                                    <Title level={4} style={{ margin: 0 }}>
                                                        {translateThis('Source')} 🎛️
                                                    </Title>
                                                    <Paragraph>&nbsp;</Paragraph>

                                                    <Form.Item
                                                        name="mode"
                                                        initialValue={formData?.mode}
                                                        style={{ marginBottom: 24 }}
                                                    >
                                                        <Radio.Group
                                                            onChange={(e) => setFormValue('mode', e.target.value)}
                                                            className="custom-radio-group"
                                                        >
                                                            <Radio value="trained">{translateThis('Trained')}</Radio>
                                                            <Radio value="gpt">{translateThis('ChatGPT')}</Radio>
                                                            <Radio value="combined">{translateThis('Combined')}</Radio>
                                                        </Radio.Group>
                                                    </Form.Item>
                                                </Col>

                                                {/* Right: GPT version */}
                                                <Col xs={24} md={12}>
                                                    <Title level={4} style={{ margin: 0 }}>
                                                        {translateThis('GPT version')}
                                                    </Title>
                                                    <Paragraph>&nbsp;</Paragraph>

                                                    <Form.Item
                                                        name="modelVersion"
                                                        initialValue={formData?.modelVersion}
                                                        style={{ marginBottom: 24 }}
                                                    >
                                                        <Radio.Group
                                                            onChange={(e) => setFormValue('modelVersion', e.target.value)}
                                                            className="custom-radio-group"
                                                        >

                                                            <Radio value="gpt-4.1">GPT&nbsp;4.1</Radio>
                                                            <Radio value="gpt-5.1">GPT&nbsp;5.1</Radio>
                                                            <Radio value="gpt-5.2">GPT&nbsp;5.2</Radio>
                                                            <Radio value="gpt-5.2-pro">GPT&nbsp;5.2-Pro</Radio>

                                                        </Radio.Group>
                                                    </Form.Item>
                                                </Col>
                                            </Row>

                                        </Col>

                                        <Col span={12}>
                                            <Title level={4} style={{ margin: '0' }}>{translateThis('Give life to your AI ...')} 🚀</Title>
                                            <Paragraph>&nbsp;</Paragraph>
                                            <Form.Item
                                                label={translateThis("Name your agent")}
                                                name="agentName"
                                                style={{ marginBottom: '52px' }}
                                            >
                                                <Input
                                                    value={formData.agentName}
                                                    onChange={(e) => setFormValue('agentName', e.target.value)}
                                                    placeholder={translateThis('Type here')} />
                                            </Form.Item>

                                        </Col>
                                        <Col span={24} style={{ marginBottom: '60px' }}>
                                            <Title level={4} style={{ margin: '0', fontWeight: '400' }}>{translateThis('How does it look like?')}</Title>
                                            <br />
                                            <Title level={5} style={{ margin: '0' }}>{translateThis('3D avatar look')}</Title>
                                            <br />
                                            <Flex className='avatar-list' horizontal gap={14} wrap>
                                                {avatarIconList.map((element, key) => (
                                                    <Button key={key}
                                                        className={`avatar-btn ${formData?.avatarImg === element.fileKey ? 'selected' : ''}`}
                                                        icon={<img src={element.iconName} alt="icon" style={{ width: '100%', height: '100%' }} />}
                                                        shape="circle" onClick={() => setFormValue('avatarImg', element.fileKey)}
                                                    />
                                                ))}
                                            </Flex>
                                            <br />
                                            <Title level={5} style={{ margin: '0' }}>{translateThis('Real human look')}</Title>
                                            <br />
                                            <Flex className='avatar-list' horizontal gap={14} wrap>
                                                {humanLookAvatar.map((element, key) => (
                                                    <Button key={key}
                                                        className={`avatar-btn ${formData?.avatarImg === element.fileKey ? 'selected' : ''}`}
                                                        icon={<img src={element.iconName} alt="icon" style={{ width: '100%', height: '100%' }} />}
                                                        shape="circle" onClick={() => setFormValue('avatarImg', element.fileKey)}
                                                    />
                                                ))}
                                            </Flex>
                                            <br />
                                            <Title level={5} style={{ margin: '0' }}>{translateThis('Bot look')}</Title>
                                            <br />
                                            <Flex className='avatar-list' horizontal gap={14} wrap>
                                                {botLookAvatar.map((element, key) => (
                                                    <Button key={key}
                                                        className={`avatar-btn ${formData?.avatarImg === element.fileKey ? 'selected' : ''}`}
                                                        icon={<img src={element.iconName} alt="icon" style={{ width: '100%', height: '100%' }} />}
                                                        shape="circle" onClick={() => setFormValue('avatarImg', element.fileKey)}
                                                    />
                                                ))}
                                            </Flex>
                                        </Col>
                                    </Row> : null}
                                {/* -------------------------------------- end of page one ----------------------------------------- */}
                                {currentFormStep === 1 ?
                                    <Row gutter={90}>

                                        <Col span={24}>
                                            <br />
                                            <Title level={4} style={{ margin: '0' }}>{translateThis('Purpose of the Module / Use case')}</Title>
                                            <Paragraph>{translateThis('Here enter the purpose of this module or the use cases for which it will be applied the AI.')}</Paragraph>
                                            <Form.Item
                                                label={translateThis("Purpose / Use case")}
                                                name="useCase"
                                                required
                                                style={{ marginBottom: '52px' }}
                                            >
                                                <Input.TextArea
                                                    value={formData.useCase}
                                                    required
                                                    onChange={(e) => setFormValue('useCase', e.target.value)}
                                                    autoSize={{ minRows: 4, maxRows: 7 }}
                                                    style={{ resize: 'vertical' }}
                                                    placeholder={translateThis('Type here')} />
                                            </Form.Item>
                                        </Col>
                                        <Col span={24}>
                                            <Title level={4} style={{ margin: '0' }}>
                                                {translateThis('Chat Behaviour 🧩')}
                                            </Title>
                                            <Paragraph>
                                                {translateThis('Choose what the bot should ask before starting the conversation.')}
                                            </Paragraph>

                                            {/* Ask Name */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '13px', marginBottom: '12px' }}>
                                                <Switch
                                                    checked={!!formData?.training?.chatBehaviour?.askName}
                                                    onChange={(checked) =>
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            training: {
                                                                ...prev.training,
                                                                chatBehaviour: {
                                                                    ...(prev.training?.chatBehaviour || {}),
                                                                    askName: checked
                                                                }
                                                            }
                                                        }))
                                                    }
                                                    style={{ backgroundColor: formData?.training?.chatBehaviour?.askName ? 'var(--Accent)' : undefined }}
                                                />
                                                <Paragraph style={{ margin: 0 }}>{translateThis('Ask User Name')}</Paragraph>
                                            </div>

                                            {/* Ask Email */}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '13px', marginBottom: '12px' }}>
                                                <Switch
                                                    checked={!!formData?.training?.chatBehaviour?.askEmail}
                                                    onChange={(checked) =>
                                                        setFormData((prev) => ({
                                                            ...prev,
                                                            training: {
                                                                ...prev.training,
                                                                chatBehaviour: {
                                                                    ...(prev.training?.chatBehaviour || {}),
                                                                    askEmail: checked
                                                                }
                                                            }
                                                        }))
                                                    }
                                                    style={{ backgroundColor: formData?.training?.chatBehaviour?.askEmail ? 'var(--Accent)' : undefined }}
                                                />
                                                <Paragraph style={{ margin: 0 }}>{translateThis('Ask User Email')}</Paragraph>
                                            </div>

                                            {/* Proactive contact collection — hidden for Sales agents and when pre-chat flow already handles it */}
                                            {formData.agentType !== 'sales' && !formData?.training?.chatBehaviour?.askName && !formData?.training?.chatBehaviour?.askEmail && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '13px', marginBottom: '12px' }}>
                                                    <Switch
                                                        checked={!!formData?.training?.chatBehaviour?.collectContactProactively}
                                                        onChange={(checked) =>
                                                            setFormData((prev) => ({
                                                                ...prev,
                                                                training: {
                                                                    ...prev.training,
                                                                    chatBehaviour: {
                                                                        ...(prev.training?.chatBehaviour || {}),
                                                                        collectContactProactively: checked
                                                                    }
                                                                }
                                                            }))
                                                        }
                                                        style={{ backgroundColor: formData?.training?.chatBehaviour?.collectContactProactively ? 'var(--Accent)' : undefined }}
                                                    />
                                                    <Paragraph style={{ margin: 0 }}>{translateThis('Proactively ask for contact info (name & email) mid-conversation')}</Paragraph>
                                                </div>
                                            )}

                                            {/* Ask Goal — hidden for Sales agents (GREET stage handles this) */}
                                            {formData.agentType !== 'sales' && (
                                                <>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '13px', marginBottom: '12px' }}>
                                                        <Switch
                                                            checked={!!formData?.training?.chatBehaviour?.askUserGoal}
                                                            onChange={(checked) =>
                                                                setFormData((prev) => ({
                                                                    ...prev,
                                                                    training: {
                                                                        ...prev.training,
                                                                        chatBehaviour: {
                                                                            ...(prev.training?.chatBehaviour || {}),
                                                                            askUserGoal: checked
                                                                        }
                                                                    }
                                                                }))
                                                            }
                                                            style={{ backgroundColor: formData?.training?.chatBehaviour?.askUserGoal ? 'var(--Accent)' : undefined }}
                                                        />
                                                        <Paragraph style={{ margin: 0 }}>
                                                            {translateThis('Show custom introduction message to User ')}
                                                        </Paragraph>
                                                    </div>
                                                    {formData?.training?.chatBehaviour?.askUserGoal ? (
                                                        <Form.Item
                                                            label={translateThis('Message')}
                                                            style={{ marginTop: '12px', marginBottom: '24px' }}
                                                        >
                                                            <Input.TextArea
                                                                value={formData?.training?.chatBehaviour?.userGoalText}
                                                                onChange={(e) =>
                                                                    setFormData((prev) => ({
                                                                        ...prev,
                                                                        training: {
                                                                            ...prev.training,
                                                                            chatBehaviour: {
                                                                                ...(prev.training?.chatBehaviour || {}),
                                                                                userGoalText: e.target.value
                                                                            }
                                                                        }
                                                                    }))
                                                                }
                                                                autoSize={{ minRows: 2, maxRows: 5 }}
                                                                placeholder={translateThis('Type here')}
                                                            />
                                                        </Form.Item>
                                                    ) : null}
                                                </>
                                            )}

                                            {/* Human Handoff */}
                                            <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '16px', marginTop: '8px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '13px', marginBottom: '12px' }}>
                                                    <Switch
                                                        checked={!!formData?.training?.chatBehaviour?.humanHandoff?.enabled}
                                                        onChange={(checked) =>
                                                            setFormData((prev) => ({
                                                                ...prev,
                                                                training: {
                                                                    ...prev.training,
                                                                    chatBehaviour: {
                                                                        ...(prev.training?.chatBehaviour || {}),
                                                                        humanHandoff: {
                                                                            ...(prev.training?.chatBehaviour?.humanHandoff || {}),
                                                                            enabled: checked
                                                                        }
                                                                    }
                                                                }
                                                            }))
                                                        }
                                                        style={{ backgroundColor: formData?.training?.chatBehaviour?.humanHandoff?.enabled ? 'var(--Accent)' : undefined }}
                                                    />
                                                    <Paragraph style={{ margin: 0 }}>{translateThis('Enable Human Agent Handoff')}</Paragraph>
                                                </div>

                                                {formData?.training?.chatBehaviour?.humanHandoff?.enabled && (
                                                    <Form.Item
                                                        label={translateThis('Agent wait timeout (minutes)')}
                                                        style={{ marginBottom: '12px', maxWidth: 240 }}
                                                    >
                                                        <InputNumber
                                                            min={1}
                                                            max={60}
                                                            value={formData?.training?.chatBehaviour?.humanHandoff?.timeoutMinutes ?? 10}
                                                            onChange={(val) =>
                                                                setFormData((prev) => ({
                                                                    ...prev,
                                                                    training: {
                                                                        ...prev.training,
                                                                        chatBehaviour: {
                                                                            ...(prev.training?.chatBehaviour || {}),
                                                                            humanHandoff: {
                                                                                ...(prev.training?.chatBehaviour?.humanHandoff || {}),
                                                                                timeoutMinutes: val
                                                                            }
                                                                        }
                                                                    }
                                                                }))
                                                            }
                                                        />
                                                    </Form.Item>
                                                )}
                                            </div>
                                        </Col>

                                        {id ? <Col span={24}>
                                            <Title level={4} style={{ margin: '0' }}>{translateThis('Upload training files')} 👟</Title>
                                            <Paragraph>{translateThis('Upload documents, conversations, or data sets that the assistant will use to learn and improve its responses. Accepted formats: .txt, .csv, .pdf, .docx.')}</Paragraph>
                                            <br />
                                            {(uploadedDocuments.length > 0) ? <div className='uploaded-file-wrp f-wrp' style={{ display: 'none' }}>
                                                <div className='flex-box ai-module-upload-box'>
                                                    {uploadedDocuments.map((img, i) => (
                                                        <div key={`${i}-${img.title}`} className='each-module-items'>
                                                            <div className='module-item'>
                                                                <span className='icon-box'> <img src={AppIcons.fileIcon} alt='file-icon' /></span>
                                                                <p>{img?.title}</p>
                                                            </div>


                                                            {/* <Button shape="circle" icon={<SVGIcons.CloseIcon />} /> */}
                                                            {/* <button type='button' onClick={() => setImages((prev) => {
                                                                const newImgArr = [...prev].filter((imgAs) => img.fileKey !== imgAs.fileKey);
                                                                return [...newImgArr];
                                                            })}>
                                                                <Delete />
                                                            </button> */}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div> : null}


                                            {/* <div className='form-img-uploader f-wrp'>
                                        <S3DocumentUploader
                                            acceptOnly='*'
                                            uploadIcon={SVGIcons.UploadFileIcon()}
                                            uploadCaption={`${translateThis('Upload files here')}`}
                                            uploadURL={`ai/modules/${id}/documents`}
                                            onUploadEnd={(file) => setUploadedDocuments((prev) => {
                                                const newImgArr = [...prev];
                                                newImgArr.push(file);
                                                return [...newImgArr];
                                            })}
                                        />
                                    </div> */}

                                            <div className='form-img-uploader f-wrp'>
                                                <MultipleFileUpload
                                                    folderId={`${id}` ?? ''}
                                                    uploadURL={`ai/modules/${id}/documents`}
                                                    uploadIcon={SVGIcons.UploadFileIcon()}
                                                    uploadCaption={`${translateThis('Upload files here')}`}
                                                    uploadedDocuments={(id ? uploadedDocuments : null) || []}
                                                    onSuccess={refreshDocuments}
                                                    onClose={refreshDocuments}
                                                    onDeleteFile={(fileName) => deleteDocument(fileName)}
                                                />
                                            </div>


                                            <br />
                                        </Col> : null}
                                        
                                        {id ? (
                                            <Col span={24}>
                                                <br />
                                                <br />
                                                <Title level={4} style={{ margin: '0' }}>{translateThis('Gmail integration')} 📧</Title>
                                                <Paragraph>{translateThis('Connect a Gmail account for this module. Emails from this account are used to train the model for this module.')}</Paragraph>
                                                {gmailLoading ? (
                                                    <Paragraph>{translateThis('Loading...')}</Paragraph>
                                                ) : gmailStatus?.connected ? (
                                                    <>
                                                        <div style={{ marginBottom: '16px' }}>
                                                            <Paragraph strong style={{ marginBottom: 4 }}>
                                                                {translateThis('Gmail connected on')} {gmailStatus?.email ?? translateThis('this account')}
                                                            </Paragraph>
                                                            <Paragraph style={{ marginBottom: 0 }}>
                                                                {gmailStatus?.lastSyncAt != null && (
                                                                    <>{translateThis('Last sync')}: {new Date(gmailStatus.lastSyncAt).toLocaleString()}</>
                                                                )}
                                                                {gmailStatus?.processedCount != null && (
                                                                    <> · {translateThis('Processed emails')}: {gmailStatus.processedCount}</>
                                                                )}
                                                            </Paragraph>
                                                        </div>
                                                        <Flex gap={8} wrap="wrap" style={{ marginBottom: '24px' }}>
                                                            <Button type="primary" onClick={handleGmailSyncNow} loading={gmailSyncing}>
                                                                {translateThis('Sync now')}
                                                            </Button>
                                                            <Button type="default" onClick={handleConnectGmail} loading={gmailConnecting}>
                                                                {translateThis('Reconnect / Change Gmail')}
                                                            </Button>
                                                            <Button danger onClick={handleGmailDisconnect} loading={gmailDisconnecting}>
                                                                {translateThis('Disconnect')}
                                                            </Button>
                                                        </Flex>
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '13px', marginBottom: '12px' }}>
                                                            <Switch
                                                                checked={!!gmailConfig.enabled}
                                                                onChange={(checked) => handleGmailConfigChange({ enabled: checked })}
                                                                style={{ backgroundColor: gmailConfig.enabled ? 'var(--Accent)' : undefined }}
                                                            />
                                                            <Paragraph style={{ margin: 0 }}>{translateThis('Use Gmail for training')}</Paragraph>
                                                        </div>
                                                        <Form.Item
                                                            label={translateThis('Sync frequency')}
                                                            style={{ marginBottom: '52px' }}
                                                        >
                                                            <Select
                                                                value={gmailConfig.syncFrequency || 'manual'}
                                                                onChange={(value) => handleGmailConfigChange({ syncFrequency: value })}
                                                                style={{ minWidth: 160 }}
                                                                options={[
                                                                    { value: 'manual', label: translateThis('Manual only') },
                                                                    { value: 'hourly', label: translateThis('Hourly') },
                                                                    { value: 'daily', label: translateThis('Daily') },
                                                                ]}
                                                            />
                                                        </Form.Item>
                                                    </>
                                                ) : (
                                                    <Form.Item style={{ marginBottom: '52px' }}>
                                                        <Button type="primary" onClick={handleConnectGmail} loading={gmailConnecting}>
                                                            {translateThis('Connect Gmail account')}
                                                        </Button>
                                                    </Form.Item>
                                                )}
                                            </Col>
                                        ) : null}

                                        <Col span={24}>
                                            <br />
                                            <br />
                                            <Title level={4} style={{ margin: '0' }}>{translateThis('Training / Fine tunning')} 🔧</Title>
                                            <Paragraph>{translateThis('Provide detailed instructions, expected behavior, logical reasoning, boundaries, and examples to guide how the assistant should interact with users. These inputs help define tone, style, and specific actions to improve performance and consistency')}</Paragraph>
                                            <Form.Item
                                                label={translateThis("Training / Fine tunning")}
                                                name={['training', 'instructions']}
                                                style={{ marginBottom: '52px' }}
                                            >
                                                <Input.TextArea
                                                    value={formData.training?.instructions}
                                                    autoSize={{ minRows: 4, maxRows: 7 }}
                                                    style={{ resize: 'vertical' }}
                                                    onChange={e => setFormValue('training', { instructions: e.target.value })}
                                                    placeholder={translateThis('Type here')} />
                                            </Form.Item>
                                        </Col>

                                        <Col span={24} key={questionAnswers.length}>

                                            <Title level={4} style={{ margin: '0' }}>{translateThis('Training Examples')}</Title>
                                            <Paragraph>&nbsp;</Paragraph>

                                            {/* <div className='training-example-box f-wrp'> */}
                                            <Row gutter={90}>
                                                <Col span={12} xs={24} sm={24} md={12} lg={12} xl={12}>
                                                    <Form.Item
                                                        label={translateThis("Training text title")}
                                                        initialValue={question}
                                                        style={{ marginBottom: '20px' }}
                                                    >
                                                        <Input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder={translateThis('Type here')} />
                                                    </Form.Item>
                                                </Col>

                                                <Col span={24}>
                                                    <Form.Item
                                                        label={translateThis("Training text content")}
                                                        initialValue={answer}
                                                        style={{ marginBottom: '20px' }}
                                                    >
                                                        <Input.TextArea value={answer} onChange={(e) => setAnswer(e.target.value)} autoSize={{ minRows: 4, maxRows: 7 }}
                                                            style={{ resize: 'vertical' }} placeholder={translateThis('Type here')} />
                                                    </Form.Item>
                                                </Col>
                                                <Col span={24}>
                                                    <Button icon={<SVGIcons.UploadFileIcon />} className='upload-file-btn' onClick={onQnAnAdd} type="primary" > {translateThis("Add")} </Button>
                                                </Col>
                                                <br />
                                                <br />
                                            </Row>
                                            {/* </div> */}







                                            {questionAnswers.length ? questionAnswers.map((q, i) =>
                                                <div className='training-example-box f-wrp' key={i} style={{ marginBottom: `${questionAnswers.length ? '16px' : '0px'}`, marginTop: '24px', paddingBottom: '24px' }}>
                                                    <Row gutter={90} >
                                                        <Col span={12} xs={24} sm={24} md={12} lg={12} xl={12}>
                                                            <Form.Item
                                                                label={translateThis("Training text title")}
                                                                initialValue={q.question}
                                                                style={{ marginBottom: '20px' }}
                                                            >
                                                                <Input value={q.question} placeholder={translateThis('Type here')} />
                                                            </Form.Item>
                                                        </Col>

                                                        <Col span={24}>
                                                            <Form.Item
                                                                label={translateThis("Training text content")}
                                                                initialValue={q.answer}
                                                                style={{ marginBottom: '20px' }}
                                                            >
                                                                <Input.TextArea value={q.answer} autoSize={{ minRows: 4, maxRows: 7 }}
                                                                    style={{ resize: 'vertical' }} placeholder={translateThis('Type here')} />
                                                            </Form.Item>
                                                        </Col>
                                                        <Col span={24}>
                                                            <Button icon={<SVGIcons.DeleteBlueIcon />} className='upload-file-btn delete-btn' onClick={() => onQnAnRemove(i)} type="primary" > {translateThis("Remove")} </Button>
                                                        </Col>
                                                    </Row>
                                                </div>
                                            ) : null}


                                        </Col>

                                        {/* Pre-prompt — hidden for Sales agents (stage prompts in salesConfig handle this) */}
                                        {formData.agentType !== 'sales' && (
                                            <Col span={24}>
                                                <br />
                                                <br />
                                                <Title level={4} style={{ margin: '0' }}>{translateThis('Setting a Pre-prompt')} 🎯</Title>
                                                <Paragraph>{translateThis("A pre-prompt is a hidden instruction included with every message the user sends to the AI agent. While the user doesn't see it, the AI agent reads it as context before responding helping shape its behavior and tone throughout the conversation.")}</Paragraph>
                                                <br />
                                                <Form.Item style={{ marginBottom: '20px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '13px', marginBottom: '20px' }}>
                                                        <Switch
                                                            checked={formData?.dynamicPromptStatus}
                                                            onChange={(checked) =>
                                                                setFormData((prev) => ({ ...prev, dynamicPromptStatus: checked }))
                                                            }
                                                            style={{ backgroundColor: formData?.dynamicPromptStatus ? 'var(--Accent)' : undefined }}
                                                        />
                                                        <Paragraph style={{ margin: '0' }}>{translateThis("Use custom prompt")}</Paragraph>
                                                    </div>
                                                    {formData?.dynamicPromptStatus ? <Form.Item
                                                        label={translateThis("Custom prompt")}
                                                        initialValue={formData.dynamicPrompt}
                                                        style={{ marginBottom: '20px' }}
                                                    >
                                                        <Input.TextArea
                                                            value={formData.dynamicPrompt}
                                                            onChange={(e) => setFormValue('dynamicPrompt', e.target.value)}
                                                            autoSize={{ minRows: 4, maxRows: 7 }}
                                                            style={{ resize: 'vertical' }}
                                                            placeholder={translateThis('Type here')} />
                                                    </Form.Item> : null}
                                                </Form.Item>
                                            </Col>
                                        )}

                                        <Col span={24}>
                                            <br />
                                            <br />
                                            <Title level={3} style={{ margin: '0' }}>{translateThis('Personality')} 🎭</Title>
                                            <Paragraph>&nbsp;</Paragraph>
                                            <Title level={4} style={{ margin: '0' }}>{translateThis('Do you want to choose a tone?')}  🗣️</Title>
                                            <br />
                                            <Form.Item
                                                name="personality"
                                                initialValue={formData?.personality}
                                                style={{ marginBottom: '52px' }}
                                            >
                                                <Radio.Group
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 12,
                                                    }}
                                                    onChange={(e) => setFormValue('personality', e.target.value)}
                                                    className="custom-radio-group"
                                                >
                                                    <Radio value="casual"><p style={{ margin: '0', fontSize: '18px', fontWeight: '500' }}>{translateThis('Casual')} <i style={{ fontStyle: 'normal', fontSize: '14px', fontWeight: '400' }}>{translateThis('(Relaxed tone, like chatting with a friend)')}</i></p></Radio>
                                                    <Radio value="professional but friendly"><p style={{ margin: '0', fontSize: '18px', fontWeight: '500' }}>{translateThis('Professional but Friendly')} <i style={{ fontStyle: 'normal', fontSize: '14px', fontWeight: '400' }}>{translateThis('(Polite and clear, without being too formal)')}</i></p></Radio>
                                                    <Radio value="motivational"><p style={{ margin: '0', fontSize: '18px', fontWeight: '500' }}>{translateThis('Motivational')} <i style={{ fontStyle: 'normal', fontSize: '14px', fontWeight: '400' }}>{translateThis('(Positive, encouraging tone to boost user confidence)')}</i></p></Radio>
                                                    <Radio value="empathetic"><p style={{ margin: '0', fontSize: '18px', fontWeight: '500' }}>{translateThis('Empathetic')} <i style={{ fontStyle: 'normal', fontSize: '14px', fontWeight: '400' }}>{translateThis('(Calming and kind, great for tough or emotional moments)')}</i></p></Radio>
                                                    <Radio value="expert advisor"><p style={{ margin: '0', fontSize: '18px', fontWeight: '500' }}>{translateThis('Expert / Advisor')} <i style={{ fontStyle: 'normal', fontSize: '14px', fontWeight: '400' }}>{translateThis('(Confident and helpful, like a knowledgeable assistant)')}</i></p></Radio>

                                                </Radio.Group>
                                            </Form.Item>

                                        </Col>

                                        {/* Temperature — hidden for Sales agents (managed internally per stage) */}
                                        {formData.agentType !== 'sales' && (
                                            <Col span={12} xs={24} sm={24} md={12} lg={12} xl={12}>
                                                <Title level={4} style={{ margin: '0' }}>{translateThis('Temperature')} 🌡️</Title>
                                                <Paragraph>
                                                    {translateThis("Set the tone of your AI Agent's responses. Adjust the creativity level from straightforward and factual to imaginative and expressive using the temperature setting.")}
                                                </Paragraph>
                                                <br />
                                                <Form.Item style={{ marginBottom: '20px' }}>
                                                    <div
                                                        className="f-wrp"
                                                        style={{
                                                            marginBottom: '20px',
                                                            maxWidth: '100%',
                                                            width: '100%',
                                                            height: '90px',
                                                            overflowX: 'hidden',
                                                            paddingLeft: '20px',
                                                            paddingRight: '35px',
                                                        }}
                                                    >
                                                        <Slider
                                                            style={{ width: '100%', }}
                                                            tooltip={{ open: true }}
                                                            className="slider-bar"
                                                            min={0}
                                                            step={0.05}
                                                            max={2}
                                                            defaultValue={formData?.parameters?.temperature || 1}
                                                            marks={marks}
                                                            onChange={(v) => {
                                                                setFormValue('parameters', { temperature: v });
                                                            }}
                                                        />
                                                    </div>
                                                </Form.Item>
                                            </Col>
                                        )}

                                        <Col span={24}>
                                            <Title level={4} style={{ margin: '0' }}>{translateThis('Language')}</Title>
                                            <Paragraph>&nbsp;</Paragraph>
                                            <Form.Item
                                                name="defaultLanguage"
                                                initialValue={formData?.defaultLanguage}
                                                style={{ marginBottom: '52px' }}
                                            >
                                                <Radio.Group
                                                    onChange={(e) => setFormValue('defaultLanguage', e.target.value)}
                                                    className="custom-radio-group"
                                                >
                                                    <Radio value="en">{translateThis('English')}</Radio>
                                                    <Radio value="es">{translateThis('Spanish')}</Radio>
                                                </Radio.Group>
                                            </Form.Item>

                                        </Col>

                                        {/* -------- Sales Training (visible only when agentType === 'sales') -------- */}
                                        {formData.agentType === 'sales' && (() => {
                                            const sf = formData.salesConfig || {};
                                            const ef = sf.enabledFields || {};
                                            const rc = sf.relevanceCheck || {};
                                            const sp = sf.stagePrompts || {};

                                            const setEnabledField = (field, val) =>
                                                setFormData(prev => ({
                                                    ...prev,
                                                    salesConfig: {
                                                        ...prev.salesConfig,
                                                        enabledFields: { ...prev.salesConfig?.enabledFields, [field]: val }
                                                    }
                                                }));

                                            const setRelevanceCheck = (field, val) =>
                                                setFormData(prev => ({
                                                    ...prev,
                                                    salesConfig: {
                                                        ...prev.salesConfig,
                                                        relevanceCheck: { ...prev.salesConfig?.relevanceCheck, [field]: val }
                                                    }
                                                }));

                                            const setStagePrompt = (stage, val) =>
                                                setFormData(prev => ({
                                                    ...prev,
                                                    salesConfig: {
                                                        ...prev.salesConfig,
                                                        stagePrompts: { ...prev.salesConfig?.stagePrompts, [stage]: val }
                                                    }
                                                }));

                                            const setSalesConfigField = (field, val) =>
                                                setFormData(prev => ({
                                                    ...prev,
                                                    salesConfig: { ...prev.salesConfig, [field]: val }
                                                }));

                                            const QUAL_FIELDS = [
                                                { key: 'interestIn', label: 'Interest In' },
                                                { key: 'goal', label: 'Goal' },
                                                { key: 'timelineUrgency', label: 'Timeline / Urgency' },
                                            ];

                                            const cardStyle = {
                                                background: 'linear-gradient(135deg, rgba(117, 23, 248, 0.05) 0%, rgba(196, 56, 239, 0.05) 100%)',
                                                border: '1px solid rgba(117, 23, 248, 0.2)',
                                                borderRadius: '12px',
                                                padding: '24px',
                                                marginBottom: '16px'
                                            };

                                            return (
                                                <Col span={24} style={{ marginTop: '32px', marginBottom: '52px' }}>

                                                    <Title level={4} style={{ margin: '0 0 4px 0' }}>
                                                        {translateThis('Sales Training')} 🤝
                                                    </Title>
                                                    <Paragraph style={{ marginBottom: '20px' }}>
                                                        {translateThis('All fields below are optional. Only configure what you need — the agent will only run the stages you define.')}
                                                    </Paragraph>

                                                    {/* Conversion Goal */}
                                                    <div style={cardStyle}>
                                                        <Title level={5} style={{ margin: '0 0 4px 0' }}>
                                                            {translateThis('Conversion Goal')} 🎯
                                                        </Title>
                                                        <Paragraph style={{ fontSize: '13px', color: 'rgba(255,255,255,0.65)', marginBottom: '12px' }}>
                                                            {translateThis('Where do you want to send qualified leads? The agent will drive toward this at the end of the conversation.')}
                                                        </Paragraph>
                                                        <Input
                                                            value={sf.conversionGoal || ''}
                                                            onChange={(e) => setSalesConfigField('conversionGoal', e.target.value)}
                                                            placeholder={translateThis('e.g. Book a free consultation call at xmed.com/schedule')}
                                                            size="large"
                                                        />
                                                    </div>

                                                    {/* Relevance Check */}
                                                    <div style={cardStyle}>
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                                                            <Title level={5} style={{ margin: 0 }}>
                                                                {translateThis('Relevance Check')} 🔍
                                                            </Title>
                                                            <Switch
                                                                checked={rc.enabled === true}
                                                                onChange={(checked) => setRelevanceCheck('enabled', checked)}
                                                            />
                                                        </div>
                                                        <Paragraph style={{ fontSize: '13px', color: 'rgba(255,255,255,0.65)', marginBottom: rc.enabled ? '16px' : 0 }}>
                                                            {translateThis('Enable to filter out leads that do not match your target audience.')}
                                                        </Paragraph>
                                                        {rc.enabled && (
                                                            <>
                                                                <Paragraph style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '6px' }}>
                                                                    {translateThis('Who is NOT a relevant lead?')}
                                                                </Paragraph>
                                                                <Input.TextArea
                                                                    value={rc.rules || ''}
                                                                    onChange={(e) => setRelevanceCheck('rules', e.target.value)}
                                                                    placeholder={translateThis('e.g. Skip users who have already passed USMLE Step 1. Only relevant if they are currently preparing for it.')}
                                                                    autoSize={{ minRows: 3, maxRows: 5 }}
                                                                    style={{ marginBottom: '16px' }}
                                                                />
                                                                <Paragraph style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '6px' }}>
                                                                    {translateThis('What should the agent do with irrelevant leads?')}
                                                                </Paragraph>
                                                                <Input.TextArea
                                                                    value={rc.irrelevantMessage || ''}
                                                                    onChange={(e) => setRelevanceCheck('irrelevantMessage', e.target.value)}
                                                                    placeholder={translateThis('e.g. Politely tell them this service is not the right fit and wish them luck. Do not push further.')}
                                                                    autoSize={{ minRows: 2, maxRows: 4 }}
                                                                />
                                                            </>
                                                        )}
                                                    </div>

                                                    {/* Advanced Settings */}
                                                    <Collapse ghost style={{ border: '1px solid rgba(117, 23, 248, 0.2)', borderRadius: '12px', overflow: 'hidden' }}>
                                                        <Collapse.Panel
                                                            key="advanced"
                                                            header={
                                                                <span style={{ color: '#fff' }}>
                                                                    {translateThis('Advanced Settings')} ⚙️
                                                                    <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginLeft: '8px' }}>
                                                                        {translateThis('Qualification fields, stage prompts — all optional')}
                                                                    </span>
                                                                </span>
                                                            }
                                                        >
                                                            {/* Qualification Fields */}
                                                            <Paragraph style={{ fontWeight: 600, marginBottom: '6px' }}>{translateThis('Data to collect from each lead')}</Paragraph>
                                                            <Paragraph style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '12px' }}>
                                                                {translateThis('The agent extracts these automatically from the conversation. Disable any you do not need.')}
                                                            </Paragraph>
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '24px' }}>
                                                                {QUAL_FIELDS.map(({ key, label }) => (
                                                                    <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                        <Switch checked={ef[key] !== false} onChange={(checked) => setEnabledField(key, checked)} />
                                                                        <span style={{ color: '#fff' }}>{translateThis(label)}</span>
                                                                    </div>
                                                                ))}
                                                            </div>

                                                            {/* Stage Prompts */}
                                                            <div style={{ borderTop: '1px solid rgba(117,23,248,0.15)', paddingTop: '16px' }}>
                                                                <Paragraph style={{ fontWeight: 600, marginBottom: '4px' }}>{translateThis('Stage instructions')}</Paragraph>
                                                                <Paragraph style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '12px' }}>
                                                                    {translateThis('Define what the agent does at each stage. Leave blank to skip that stage or rely on built-in behaviour.')}
                                                                </Paragraph>
                                                                {[
                                                                    { key: 'greet', label: '1. Greet', placeholder: 'e.g. Welcome the user warmly and ask an open question.' },
                                                                    { key: 'relevanceCheck', label: '2. Relevance Check', placeholder: 'e.g. Gently probe whether the user matches your target audience.' },
                                                                    { key: 'qualify', label: '3. Qualify', placeholder: 'e.g. Uncover the lead\'s interest, goal, and timeline naturally.' },
                                                                    { key: 'persuade', label: '4. Persuade', placeholder: 'e.g. Highlight benefits relevant to what the lead shared.' },
                                                                    { key: 'convert', label: '5. Convert', placeholder: 'e.g. Make a confident, clear call to action.' },
                                                                ].map(({ key, label, placeholder }) => (
                                                                    <div key={key} style={{ marginBottom: '12px' }}>
                                                                        <Paragraph style={{ marginBottom: '4px', fontSize: '13px', fontWeight: 500 }}>{translateThis(label)}</Paragraph>
                                                                        <Input.TextArea value={sp[key] || ''} onChange={(e) => setStagePrompt(key, e.target.value)} placeholder={translateThis(placeholder)} autoSize={{ minRows: 2, maxRows: 4 }} />
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </Collapse.Panel>
                                                    </Collapse>

                                                </Col>
                                            );
                                        })()}

                                    </Row> : null}
                                {/* -------------------------------------- end of page two ----------------------------------------- */}
                                {currentFormStep === 2 ?
                                    <Row gutter={90}>
                                        <Col span={24}>
                                            <br />
                                            <br />
                                            <Title level={3} style={{ margin: '0' }}>{translateThis('Customize your chatbot’s look and feel')} 🧑‍🎨 </Title>
                                            <Paragraph>&nbsp;</Paragraph>
                                            <Title level={4} style={{ margin: '0' }}>{translateThis('Choose your Avatar')}</Title>
                                            <br />

                                            <Flex className='avatar-list' horizontal gap={14} wrap>
                                                {avatarIconList.map((element, key) => (
                                                    <Button key={key}
                                                        className={`avatar-btn ${formData?.avatarImg === element.fileKey ? 'selected' : ''}`}
                                                        icon={<img src={element.iconName} alt="icon" style={{ width: '100%', height: '100%' }} />}
                                                        shape="circle" onClick={() => setFormValue('avatarImg', element.fileKey)}
                                                    />
                                                ))}
                                            </Flex>

                                            <br />
                                            <Title level={5} style={{ margin: '0' }}>{translateThis('Real human look')}</Title>
                                            <br />
                                            <Flex className='avatar-list' horizontal gap={14} wrap>
                                                {humanLookAvatar.map((element, key) => (
                                                    <Button key={key}
                                                        className={`avatar-btn ${formData?.avatarImg === element.fileKey ? 'selected' : ''}`}
                                                        icon={<img src={element.iconName} alt="icon" style={{ width: '100%', height: '100%' }} />}
                                                        shape="circle" onClick={() => setFormValue('avatarImg', element.fileKey)}
                                                    />
                                                ))}
                                            </Flex>
                                            <br />
                                            <Title level={5} style={{ margin: '0' }}>{translateThis('Bot look')}</Title>
                                            <br />
                                            <Flex className='avatar-list' horizontal gap={14} wrap>
                                                {botLookAvatar.map((element, key) => (
                                                    <Button key={key}
                                                        className={`avatar-btn ${formData?.avatarImg === element.fileKey ? 'selected' : ''}`}
                                                        icon={<img src={element.iconName} alt="icon" style={{ width: '100%', height: '100%' }} />}
                                                        shape="circle" onClick={() => setFormValue('avatarImg', element.fileKey)}
                                                    />
                                                ))}
                                            </Flex>
                                        </Col>
                                        <Col span={24}>
                                            <br />
                                            <Paragraph>&nbsp;</Paragraph>
                                            <Title level={3} style={{ margin: '0' }}>{translateThis('Rotating avatar + logo')} 🖼️</Title>
                                            <Paragraph>&nbsp;</Paragraph>

                                            <Form.Item style={{ marginBottom: '20px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '13px', marginBottom: '20px' }}>
                                                    <Switch
                                                        checked={formData?.isAddRotatingAvatarAndLogo}
                                                        onChange={(checked) =>
                                                            setFormData((prev) => ({ ...prev, isAddRotatingAvatarAndLogo: checked }))
                                                        }
                                                        style={{ backgroundColor: formData?.isAddRotatingAvatarAndLogo ? 'var(--Accent)' : undefined }}
                                                    />
                                                    <Paragraph style={{ margin: '0' }}>{translateThis("Add rotating avatar + logo")}</Paragraph>
                                                </div>

                                            </Form.Item>

                                            {/* <div className='logo-upload-box f-wrp'>
                                                <div className='upload-preview'>
                                                    <span>
                                                        <img src={AppIcons.defaultLogo} alt='default logo' />
                                                    </span>
                                                    <p>{translateThis('Default image')}</p>
                                                </div>

                                                <div className='form-img-uploader f-wrp'>
                                                    <S3DocumentUploader
                                                        acceptOnly='image/*'
                                                        uploadIcon={SVGIcons.UploadFileIcon()}
                                                        uploadCaption={`${translateThis('Add logo')}`}
                                                        uploadURL={`ai/modules`}
                                                        onUploadEnd={(file) => { setUploadedLogo(file); console.log(file, 'file') }}
                                                    />
                                                </div>

                                            </div> */}




                                            <div className='logo-upload-box rotateIconUpload f-wrp'>
                                                <LogoPreview
                                                    uploadedLogo={uploadedLogo}
                                                    formData={formData}
                                                />
                                                <div className="form-img-uploader f-wrp">
                                                    <div className='uploader-box upload-file-btn'>
                                                        <input
                                                            type="file"
                                                            accept="image/*"
                                                            onChange={handleLogoUpload}
                                                            style={{ display: "none" }}
                                                            id="logoUpload"
                                                        />

                                                        <label htmlFor="logoUpload" className="upload-btn">
                                                            {SVGIcons.UploadFileIcon()}
                                                            {translateThis("Add logo")}
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>


                                        </Col>
                                        <Col span={24}>
                                            <br />
                                            <Paragraph>&nbsp;</Paragraph>
                                            <Title level={3} style={{ margin: '0' }}>{translateThis('Top bar & title')}  📐 </Title>
                                            <Paragraph>&nbsp;</Paragraph>

                                            <Form.Item style={{ marginBottom: '20px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '13px', marginBottom: '20px' }}>
                                                    <Switch
                                                        checked={formData?.isHeaderPresent}
                                                        onChange={(checked) =>
                                                            setFormData((prev) => ({ ...prev, isHeaderPresent: checked }))
                                                        }
                                                        style={{ backgroundColor: formData?.isHeaderPresent ? 'var(--Accent)' : undefined }}
                                                    />
                                                    <Paragraph style={{ margin: '0' }}>{translateThis("Add top bar theme ")}( {formData?.isHeaderPresent ? <>{translateThis("On")}</> : <>{translateThis("Off")}</>} )</Paragraph>
                                                </div>

                                            </Form.Item>



                                            {/* <Paragraph>&nbsp;</Paragraph> */}
                                            <Title level={4} style={{ margin: '0', fontWeight: '400' }}>{translateThis('Theme presets')}</Title>
                                            <Paragraph>&nbsp;</Paragraph>
                                            <br />


                                            {/* <Row gutter={70}>
                                        <Col span={9}>
                                            <div className='f-wrp' style={{ height: '100%' }}>
                                                <img className='chat-preview' src={AppIcons.chatPreview} alt="icon" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                            </div>

                                        </Col>
                                        <Col span={15}>

                                            <Form.Item
                                                name="chatHeader"
                                                initialValue={formData?.chatHeader}
                                                style={{ marginBottom: '0px' }}
                                            >
                                                <Radio.Group
                                                    style={{ display: 'flex', flexWrap: 'wrap' }}
                                                    onChange={(e) => setFormValue('chatHeader', e.target.value)}
                                                    className="custom-radio-group"
                                                >
                                                    <Radio style={{ flex: '0 0 50%', maxWidth: '50%', margin: '0 0 30px 0' }} value="header1"><img src={AppIcons.chatHead1} alt="icon" style={{ width: '100%', height: '100%' }} /></Radio>
                                                    <Radio style={{ flex: '0 0 50%', maxWidth: '50%', margin: '0 0 30px 0' }} value="header2"><img src={AppIcons.chatHead2} alt="icon" style={{ width: '100%', height: '100%' }} /></Radio>
                                                    <Radio style={{ flex: '0 0 50%', maxWidth: '50%', margin: '0 0 30px 0' }} value="header3"><img src={AppIcons.chatHead3} alt="icon" style={{ width: '100%', height: '100%' }} /></Radio>
                                                    <Radio style={{ flex: '0 0 50%', maxWidth: '50%', margin: '0 0 30px 0' }} value="header4"><img src={AppIcons.chatHead4} alt="icon" style={{ width: '100%', height: '100%' }} /></Radio>
                                                    <Radio style={{ flex: '0 0 50%', maxWidth: '50%', margin: '0 0 30px 0' }} value="header5"><img src={AppIcons.chatHead5} alt="icon" style={{ width: '100%', height: '100%' }} /></Radio>
                                                    <Radio style={{ flex: '0 0 50%', maxWidth: '50%', margin: '0 0 30px 0' }} value="header6"><img src={AppIcons.chatHead6} alt="icon" style={{ width: '100%', height: '100%' }} /></Radio>
                                                    <Radio style={{ flex: '0 0 50%', maxWidth: '50%', margin: '0 0 0px 0' }} value="header7"><img src={AppIcons.chatHead7} alt="icon" style={{ width: '100%', height: '100%' }} /></Radio>
                                                </Radio.Group>
                                            </Form.Item>
                                        </Col>
                                    </Row> */}

                                            <Row gutter={70}>
                                                <Col span={9}>
                                                    <div className='f-wrp chat-preview-box chat-header-box'>
                                                        <div className={`header ${formData?.isHeaderPresent ? 'header-added' : 'header-removed'}`}>
                                                            {(formData?.isHeaderPresent) ? <img
                                                                className='chat-head'
                                                                src={mergedHeaders[selectedHeader]}
                                                                alt="Selected preview"
                                                            /> : ''}

                                                            <div className='title-box'>
                                                                <div className="logo-upload-box f-wrp">
                                                                    <LogoPreview
                                                                        uploadedLogo={uploadedLogo}
                                                                        formData={formData}
                                                                    />
                                                                </div>


                                                                <p >Conversation box title</p>

                                                            </div>


                                                            {/* <img src={AppIcons.mobileThemeHeadLogo} alt='logo' /> */}
                                                            <div className='head-btns'>
                                                                <SVGIcons.ChatThemeMaximizeIcon />
                                                                <SVGIcons.ChatThemeCloseIcon />
                                                            </div>

                                                        </div>

                                                        {/* <img
                                                            className='chat-head'
                                                            src={headerImages[selectedHeader]}
                                                            alt="Selected preview"
                                                        /> */}
                                                        <img className='chat-preview' src={AppIcons.chatPreview} alt="icon" />

                                                    </div>
                                                </Col>

                                                <Col span={15}>
                                                    <Form.Item
                                                        name="chatHeaderImg"
                                                        initialValue={formData?.chatHeaderImg}
                                                        style={{ marginBottom: '0px' }}
                                                    >
                                                        <Radio.Group
                                                            style={{ display: 'flex', flexWrap: 'wrap' }}
                                                            onChange={handleHeaderChange}
                                                            className="custom-radio-group"
                                                            value={selectedHeader} // Controlled value
                                                        >
                                                            {Object.entries(headerImages).map(([key, src]) => (
                                                                <Radio
                                                                    key={key}
                                                                    value={key}
                                                                    style={{ flex: '0 0 48%', maxWidth: '48%', marginBottom: '30px' }}
                                                                >
                                                                    <img src={src} alt={`icon-${key}`} style={{ width: '100%', height: '100%' }} />
                                                                </Radio>
                                                            ))}
                                                            {/* <Radio
                                                                className='uploadRadio'
                                                                key='customBGHeader'
                                                                value='customBGHeader'
                                                                style={{ flex: '0 0 48%', maxWidth: '48%', marginBottom: '30px' }}
                                                            >
                                                                <ImgCrop rotationSlider aspectSlider modalWidth={'767px'}>
                                                                    <Upload
                                                                        className='upload-box'
                                                                        customRequest={formData.customChatHeader}
                                                                        listType="picture-card"
                                                                        fileList={fileList}
                                                                        onChange={onChange}
                                                                        onPreview={onPreview}
                                                                        maxCount={1}
                                                                        tooltip={false}
                                                                    >
                                                                        {'+ Upload your background'}
                                                                    </Upload>
                                                                </ImgCrop>
                                                            </Radio> */}

                                                            {/* <Radio
                                                                className='uploadRadio'
                                                                key='customBGHeader'
                                                                value='customBGHeader'
                                                                style={{ flex: '0 0 48%', maxWidth: '48%', marginBottom: '30px' }}
                                                            >
                                                                <div className='bg-uploader-box'>
                                                                    <ImageCropUploader
                                                                        labelTitle={'+ Upload your background'}
                                                                        aspect={355 / 55}
                                                                        maxSize={3}
                                                                        onUpload={(img) =>
                                                                            setFormData((prev) => ({
                                                                                ...prev,
                                                                                customChatHeader: img,
                                                                            }))
                                                                        }

                                                                    // apiUpload={async (img) => {
                                                                    //     await fetch("/api/upload", {
                                                                    //         method: "POST",
                                                                    //         body: JSON.stringify({ image: img }),
                                                                    //     });
                                                                    // }}
                                                                    />
                                                                </div>
                                                            </Radio> */}




                                                            <Radio
                                                                className='uploadRadio'
                                                                key='customBGHeader'
                                                                value='customBGHeader'
                                                                style={{ flex: '0 0 48%', maxWidth: '48%', marginBottom: '30px' }}
                                                            >
                                                                <div className='bg-uploader-box'>
                                                                    <ImageCropUploader
                                                                        labelTitle={'+ Upload your background'}
                                                                        aspect={355 / 55}
                                                                        maxSize={3}
                                                                        onUpload={(img) => {

                                                                            setFormData((prev) => ({
                                                                                ...prev,
                                                                                customChatHeader: img,
                                                                            }));


                                                                            setSelectedHeader('customBGHeader');
                                                                            setFormValue('chatHeaderImg', 'customBGHeader');
                                                                        }}
                                                                    // apiUpload={async (img) => {
                                                                    //     await fetch("/api/upload", {
                                                                    //         method: "POST",
                                                                    //         body: JSON.stringify({ image: img }),
                                                                    //     });
                                                                    // }}

                                                                    />
                                                                </div>
                                                            </Radio>

                                                        </Radio.Group>
                                                    </Form.Item>





                                                </Col>
                                            </Row>

                                        </Col>
                                        <Col span={12} >
                                            <br />
                                            <br />
                                            <Title level={4} style={{ margin: '0', fontWeight: '400' }}>{translateThis('Conversation box title')}</Title>
                                            <Paragraph>&nbsp;</Paragraph>

                                            <Form.Item
                                                label={translateThis("Title")}
                                                name="conversationBoxTitle"
                                                style={{ marginBottom: '52px' }}
                                            >
                                                <Input
                                                    value={formData.conversationBoxTitle}
                                                    onChange={(e) => setFormValue('conversationBoxTitle', e.target.value)}
                                                    placeholder={translateThis('Type here')} />
                                            </Form.Item>
                                        </Col>
                                        <Col span={24} >
                                            <br />
                                            <Title level={4} style={{ margin: '0' }}>{translateThis('Background color')}  🎨</Title>
                                            <br />
                                            <br />

                                            <Form.Item
                                                name="themeBackgroundColor"
                                                initialValue={formData?.themeBackgroundColor}
                                                className='themeBGSelector'
                                                style={{ marginBottom: '52px' }}
                                            >
                                                <Radio.Group
                                                    style={{ display: 'flex', flexWrap: 'wrap' }}
                                                    onChange={(e) => setFormValue('themeBackgroundColor', e.target.value)}
                                                    className="custom-radio-group"
                                                >

                                                    <Radio value="themeBGLight">
                                                        <div className='mobileThemeBG themeBGLight'>
                                                            <div className={`header ${formData?.isHeaderPresent ? 'header-added' : 'header-removed'}`} >
                                                                <img
                                                                    className='chat-head'
                                                                    src={mergedHeaders[selectedHeader]}
                                                                    alt="Selected preview"
                                                                />

                                                                <div className='title-box mobile-titleBox'>
                                                                    <div className="logo-upload-box f-wrp">
                                                                        <LogoPreview
                                                                            uploadedLogo={uploadedLogo}
                                                                            formData={formData}
                                                                        />
                                                                    </div>
                                                                    <span>{formData.conversationBoxTitle ? <>{formData.conversationBoxTitle}</> : <>PnP</>}</span>

                                                                </div>



                                                                {/* <img src={AppIcons.mobileThemeHeadLogo} alt='logo' /> */}
                                                                <div className='head-btns'>
                                                                    {/* <SVGIcons.ChatThemeMaximizeIcon /> */}
                                                                    <SVGIcons.ChatThemeCloseIcon />
                                                                </div>
                                                                {/* <img src={AppIcons.mobileThemeHeadLogo} alt='logo' />
                                                                <img src={AppIcons.mobileThemeHeadClose} alt='close' /> */}
                                                            </div>
                                                            <div className='message-box'>
                                                                <div className='user-msg'>
                                                                    <span>Hello, how are you doing?</span>
                                                                </div>
                                                                <div className='bot-msg'>
                                                                    {formData?.avatarImg ? <div className='avatar-preview'>
                                                                        <AvatarViewer className='chat-head' avatar={formData?.avatarImg} />
                                                                    </div> : null}
                                                                    <span>I'm doing well, thank you! How can I help <br />you today?</span>
                                                                </div>
                                                            </div>
                                                            <div className='chat-box'>
                                                                <img src={AppIcons.mobileThemeChatInput} alt='chat' />
                                                            </div>
                                                        </div>
                                                        <p>{translateThis('Light')}</p>
                                                    </Radio>
                                                    <Radio value="themeBGGrey">
                                                        {/* <img src={AppIcons.themeBGGrey} className='themeBGSelect' alt="background color" /> */}
                                                        <div className='mobileThemeBG themeBGGrey'>
                                                            <div className={`header ${formData?.isHeaderPresent ? 'header-added' : 'header-removed'}`} >
                                                                <img
                                                                    className='chat-head'
                                                                    src={mergedHeaders[selectedHeader]}
                                                                    alt="Selected preview"
                                                                />
                                                                <div className='title-box mobile-titleBox'>
                                                                    <div className="logo-upload-box f-wrp">
                                                                        <LogoPreview
                                                                            uploadedLogo={uploadedLogo}
                                                                            formData={formData}
                                                                        />
                                                                    </div>

                                                                    <span>{formData.conversationBoxTitle ? <>{formData.conversationBoxTitle}</> : <>PnP</>}</span>

                                                                </div>
                                                                <div className='head-btns'>
                                                                    <SVGIcons.ChatThemeCloseIcon />
                                                                </div>
                                                            </div>
                                                            <div className='message-box'>
                                                                <div className='user-msg'>
                                                                    <span>Hello, how are you doing?</span>
                                                                </div>
                                                                <div className='bot-msg'>
                                                                    {formData?.avatarImg ? <div className='avatar-preview'>
                                                                        <AvatarViewer className='chat-head' avatar={formData?.avatarImg} />
                                                                    </div> : null}
                                                                    <span>I'm doing well, thank you! How can I help <br />you today?</span>
                                                                </div>
                                                            </div>
                                                            <div className='chat-box'>
                                                                <img src={AppIcons.mobileThemeChatInput} alt='chat' />
                                                            </div>
                                                        </div>
                                                        <p>{translateThis('Grey')}</p>
                                                    </Radio>
                                                    <Radio value="themeBGDark">
                                                        {/* <img src={AppIcons.themeBGDark} className='themeBGSelect' alt="background color" /> */}
                                                        <div className='mobileThemeBG themeBGDark'>
                                                            <div className={`header ${formData?.isHeaderPresent ? 'header-added' : 'header-removed'}`} >
                                                                <img
                                                                    className='chat-head'
                                                                    src={mergedHeaders[selectedHeader]}
                                                                    alt="Selected preview"
                                                                />
                                                                <div className='title-box mobile-titleBox'>
                                                                    <div className="logo-upload-box f-wrp">
                                                                        <LogoPreview
                                                                            uploadedLogo={uploadedLogo}
                                                                            formData={formData}
                                                                        />
                                                                    </div>

                                                                    <span>{formData.conversationBoxTitle ? <>{formData.conversationBoxTitle}</> : <>PnP</>}</span>

                                                                </div>
                                                                <div className='head-btns'>
                                                                    <SVGIcons.ChatThemeCloseIcon />
                                                                </div>
                                                            </div>
                                                            <div className='message-box'>
                                                                <div className='user-msg'>
                                                                    <span>Hello, how are you doing?</span>
                                                                </div>
                                                                <div className='bot-msg'>
                                                                    {formData?.avatarImg ? <div className='avatar-preview'>
                                                                        <AvatarViewer className='chat-head' avatar={formData?.avatarImg} />
                                                                    </div> : null}
                                                                    <span>I'm doing well, thank you! How can I help <br />you today?</span>
                                                                </div>
                                                            </div>
                                                            <div className='chat-box'>
                                                                <img src={AppIcons.mobileThemeChatInput} alt='chat' />
                                                            </div>
                                                        </div>
                                                        <p>{translateThis('Dark')}</p>
                                                    </Radio>
                                                    <Radio value="themeBGColor">
                                                        {/* <img src={AppIcons.themeBGColor} className='themeBGSelect' alt="background color" /> */}
                                                        <div className='mobileThemeBG themeBGColor'>
                                                            <div className={`header ${formData?.isHeaderPresent ? 'header-added' : 'header-removed'}`} >
                                                                <img
                                                                    className='chat-head'
                                                                    src={mergedHeaders[selectedHeader]}
                                                                    alt="Selected preview"
                                                                />
                                                                <div className='title-box mobile-titleBox'>
                                                                    <div className="logo-upload-box f-wrp">
                                                                        <LogoPreview
                                                                            uploadedLogo={uploadedLogo}
                                                                            formData={formData}
                                                                        />
                                                                    </div>

                                                                    <span>{formData.conversationBoxTitle ? <>{formData.conversationBoxTitle}</> : <>PnP</>}</span>

                                                                </div>
                                                                <div className='head-btns'>
                                                                    <SVGIcons.ChatThemeCloseIcon />
                                                                </div>
                                                            </div>
                                                            <div className='message-box'>
                                                                <div className='user-msg'>
                                                                    <span>Hello, how are you doing?</span>
                                                                </div>
                                                                <div className='bot-msg'>
                                                                    {formData?.avatarImg ? <div className='avatar-preview'>
                                                                        <AvatarViewer className='chat-head' avatar={formData?.avatarImg} />
                                                                    </div> : null}
                                                                    <span>I'm doing well, thank you! How can I help <br />you today?</span>
                                                                </div>
                                                            </div>
                                                            <div className='chat-box'>
                                                                <img src={AppIcons.mobileThemeChatInput} alt='chat' />
                                                            </div>
                                                        </div>
                                                        <p>{translateThis('Color')}</p>
                                                    </Radio>
                                                    <Radio value="themeBGFlash">
                                                        {/* <img src={AppIcons.themeBGFlash} className='themeBGSelect' alt="background color" /> */}
                                                        <div className='mobileThemeBG themeBGFlash'>
                                                            <div className={`header ${formData?.isHeaderPresent ? 'header-added' : 'header-removed'}`} >
                                                                <img
                                                                    className='chat-head'
                                                                    src={mergedHeaders[selectedHeader]}
                                                                    alt="Selected preview"
                                                                />
                                                                <div className='title-box mobile-titleBox'>
                                                                    <div className="logo-upload-box f-wrp">
                                                                        <LogoPreview
                                                                            uploadedLogo={uploadedLogo}
                                                                            formData={formData}
                                                                        />
                                                                    </div>

                                                                    <span>{formData.conversationBoxTitle ? <>{formData.conversationBoxTitle}</> : <>PnP</>}</span>

                                                                </div>
                                                                <div className='head-btns'>
                                                                    <SVGIcons.ChatThemeCloseIcon />
                                                                </div>
                                                            </div>
                                                            <div className='message-box'>
                                                                <div className='user-msg'>
                                                                    <span>Hello, how are you doing?</span>
                                                                </div>
                                                                <div className='bot-msg'>
                                                                    {formData?.avatarImg ? <div className='avatar-preview'>
                                                                        <AvatarViewer className='chat-head' avatar={formData?.avatarImg} />
                                                                    </div> : null}
                                                                    <span>I'm doing well, thank you! How can I help <br />you today?</span>
                                                                </div>
                                                            </div>
                                                            <div className='chat-box'>
                                                                <img src={AppIcons.mobileThemeChatInput} alt='chat' />
                                                            </div>
                                                        </div>
                                                        <p>{translateThis('Flash')}</p>
                                                    </Radio>
                                                    <Radio value="themeBGDarkPurple">
                                                        {/* <img src={AppIcons.themeBGDarkPurple} className='themeBGSelect' alt="background color" /> */}
                                                        <div className='mobileThemeBG themeBGDarkPurple'>
                                                            <div className={`header ${formData?.isHeaderPresent ? 'header-added' : 'header-removed'}`} >
                                                                <img
                                                                    className='chat-head'
                                                                    src={mergedHeaders[selectedHeader]}
                                                                    alt="Selected preview"
                                                                />
                                                                <div className='title-box mobile-titleBox'>
                                                                    <div className="logo-upload-box f-wrp">
                                                                        <LogoPreview
                                                                            uploadedLogo={uploadedLogo}
                                                                            formData={formData}
                                                                        />
                                                                    </div>

                                                                    <span>{formData.conversationBoxTitle ? <>{formData.conversationBoxTitle}</> : <>PnP</>}</span>

                                                                </div>
                                                                <div className='head-btns'>
                                                                    <SVGIcons.ChatThemeCloseIcon />
                                                                </div>
                                                            </div>
                                                            <div className='message-box'>
                                                                <div className='user-msg'>
                                                                    <span>Hello, how are you doing?</span>
                                                                </div>
                                                                <div className='bot-msg'>
                                                                    {formData?.avatarImg ? <div className='avatar-preview'>
                                                                        <AvatarViewer className='chat-head' avatar={formData?.avatarImg} />
                                                                    </div> : null}
                                                                    <span>I'm doing well, thank you! How can I help <br />you today?</span>
                                                                </div>
                                                            </div>
                                                            <div className='chat-box'>
                                                                <img src={AppIcons.mobileThemeChatInput} alt='chat' />
                                                            </div>
                                                        </div>
                                                        <p>{translateThis('Dark Purple')}</p>
                                                    </Radio>

                                                </Radio.Group>
                                            </Form.Item>
                                        </Col>
                                        <Col span={24} >
                                            <br />
                                            <br />
                                            <Title level={3} style={{ margin: '0' }}>{translateThis('Preview your virtual Assistant')} 🎬</Title>
                                            <Paragraph>{translateThis('Your bot, ready to meet the world.')}</Paragraph>

                                            <Form.Item
                                                name="selectPreview"
                                                initialValue={formData?.selectPreview}
                                                style={{ marginBottom: '52px' }}
                                            >
                                                <Radio.Group
                                                    onChange={(e) => setFormValue('selectPreview', e.target.value)}
                                                >
                                                    <Radio value="desktop">{translateThis("Desktop")}</Radio>
                                                    <Radio value="mobile">{translateThis("Mobile")}</Radio>
                                                </Radio.Group>
                                            </Form.Item>


                                            {(formData?.selectPreview === 'desktop') ? <Row gutter={70}>
                                                <Col span={14}>

                                                    <div className='chat-preview-window f-wrp'>
                                                        <img className='chat-preview-smScreen' src={AppIcons.preview1} alt="icon" />
                                                        {formData?.avatarImg ? <div className='avatar-preview'>
                                                            <div className="logo-upload-box f-wrp">
                                                                <LogoPreview
                                                                    uploadedLogo={uploadedLogo}
                                                                    formData={formData}
                                                                />
                                                            </div>

                                                            {/* <img className='chat-head' src={selectedAvatar?.iconName} alt="Selected preview" /> */}
                                                        </div> : null}
                                                    </div>

                                                    {/* <div className='f-wrp' style={{ height: '100%' }}>
                                                <img className='chat-preview' src={AppIcons.preview1} alt="icon" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                            </div> */}

                                                </Col>
                                                <Col span={10}>
                                                    <div className='chat-preview-window f-wrp'>
                                                        <img className='chat-preview-smScreen' src={AppIcons.preview2} alt="icon" />
                                                        {formData?.avatarImg ? <div className='avatar-preview'>
                                                            <div className="logo-upload-box f-wrp">
                                                                <LogoPreview
                                                                    uploadedLogo={uploadedLogo}
                                                                    formData={formData}
                                                                />
                                                            </div>

                                                            {/* <AvatarViewer className='chat-head' avatar={formData?.avatarImg} /> */}
                                                            {/* <img className='chat-head' src={selectedAvatar?.iconName} alt="Selected preview" /> */}
                                                        </div> : null}

                                                        <div className='f-wrp chat-preview-box' style={{ backgroundColor: '#261e35', borderRadius: '17px 17px 10px 10px' }}>

                                                            <div className={`mobileThemeBG ${formData?.themeBackgroundColor}`}>
                                                                <div className={`header ${formData?.isHeaderPresent ? 'header-added' : 'header-removed'}`} >
                                                                    <img
                                                                        className='chat-head'
                                                                        src={mergedHeaders[selectedHeader]}
                                                                        alt="Selected preview"
                                                                    />

                                                                    <div className='title-box mobile-titleBox'>
                                                                        <div className="logo-upload-box f-wrp">
                                                                            <LogoPreview
                                                                                uploadedLogo={uploadedLogo}
                                                                                formData={formData}
                                                                            />
                                                                        </div>

                                                                        <span>{formData.conversationBoxTitle ? <>{formData.conversationBoxTitle}</> : <>PnP</>}</span>
                                                                    </div>
                                                                    <div className='head-btns'>
                                                                        <SVGIcons.ChatThemeCloseIcon />
                                                                    </div>
                                                                </div>
                                                                <div className='message-box'>
                                                                    <div className='user-msg'>
                                                                        <span>Hello, how are you doing?</span>
                                                                    </div>
                                                                    <div className='bot-msg'>
                                                                        {formData?.avatarImg ? <div className='avatar-preview'>
                                                                            <AvatarViewer className='chat-head' avatar={formData?.avatarImg} />
                                                                        </div> : null}
                                                                        <span>I'm doing well, thank you! How can I help <br />you today?</span>
                                                                    </div>
                                                                </div>
                                                                <div className='chat-box'>
                                                                    <img src={AppIcons.mobileThemeChatInput} alt='chat' />
                                                                </div>
                                                            </div>

                                                            {/* <img
                                                                className='chat-head'
                                                                src={headerImages[selectedHeader]} // Dynamically show selected image
                                                                alt="Selected preview"
                                                            /> */}
                                                            <img className='chat-preview' src={AppIcons.ChatboxPreview} alt="icon" />
                                                        </div>
                                                    </div>
                                                </Col>
                                                <Col span={24}>

                                                    <div className={`chat-preview-window full-screen-preview f-wrp ${formData?.themeBackgroundColor}`}>
                                                        <img className='chat-preview-fullScreen' src={AppIcons.preview3} alt="icon" />
                                                        {formData?.avatarImg ? <div className='avatar-preview'>
                                                            <div className="logo-upload-box f-wrp">
                                                                <LogoPreview
                                                                    uploadedLogo={uploadedLogo}
                                                                    formData={formData}
                                                                />
                                                            </div>


                                                            {/* <AvatarViewer className='chat-head' avatar={formData?.avatarImg} /> */}
                                                            {/* <img className='chat-head' src={selectedAvatar?.iconName} alt="Selected preview" /> */}
                                                        </div> : null}
                                                        <div className='f-wrp chat-preview-box'>
                                                            {/* <img
                                                                className='chat-head'
                                                                src={headerImages[selectedHeader]} // Dynamically show selected image
                                                                alt="Selected preview"
                                                            /> */}
                                                            <div className={`mobileThemeBG ${formData?.themeBackgroundColor}`}>
                                                                {/* <div className='header' >
                                                                    <img
                                                                        className='chat-head'
                                                                        src={headerImages[selectedHeader]}
                                                                        alt="Selected preview"
                                                                    />
                                                                    <img src={AppIcons.mobileThemeHeadLogo} alt='logo' />
                                                                    <img src={AppIcons.mobileThemeHeadClose} alt='close' />
                                                                </div> */}
                                                                <div className={`header ${formData?.isHeaderPresent ? 'header-added' : 'header-removed'}`} >
                                                                    <img
                                                                        className='chat-head'
                                                                        src={mergedHeaders[selectedHeader]}
                                                                        alt="Selected preview"
                                                                    />
                                                                    <div className='title-box mobile-titleBox'>
                                                                        <div className="logo-upload-box f-wrp">
                                                                            <LogoPreview
                                                                                uploadedLogo={uploadedLogo}
                                                                                formData={formData}
                                                                            />
                                                                        </div>

                                                                        <span>{formData.conversationBoxTitle ? <>{formData.conversationBoxTitle}</> : <>PnP</>}</span>
                                                                    </div>
                                                                    <div className='head-btns'>
                                                                        <SVGIcons.ChatThemeMaximizeIcon />
                                                                        <SVGIcons.ChatThemeCloseIcon />
                                                                    </div>
                                                                </div>
                                                                <div className='message-box'>
                                                                    <div className='user-msg'>
                                                                        <span>Hello, how are you doing?</span>
                                                                    </div>
                                                                    <div className='bot-msg'>
                                                                        {formData?.avatarImg ? <div className='avatar-preview'>
                                                                            <AvatarViewer className='chat-head' avatar={formData?.avatarImg} />
                                                                        </div> : null}
                                                                        <span>I'm doing well, thank you! How can I help <br />you today?</span>
                                                                    </div>
                                                                </div>
                                                                <div className='chat-box'>
                                                                    <img src={AppIcons.mobileThemeChatInput} alt='chat' />
                                                                </div>
                                                            </div>

                                                            <img className='chat-preview' src={AppIcons.ChatWindowPreview} alt="icon" />
                                                        </div>
                                                    </div>

                                                </Col>
                                            </Row> : <Row gutter={70}>
                                                <Col span={14}>
                                                    <div className='mobile-preview-box'>
                                                        <div className={`mobileThemeBG ${formData?.themeBackgroundColor}`}>
                                                            <div className={`header ${formData?.isHeaderPresent ? 'header-added' : 'header-removed'}`} >
                                                                <img
                                                                    className='chat-head'
                                                                    src={mergedHeaders[selectedHeader]}
                                                                    alt="Selected preview"
                                                                />

                                                                <div className='title-box mobile-titleBox'>

                                                                    <span>{formData.conversationBoxTitle ? <>{formData.conversationBoxTitle}</> : <>PnP</>}</span>
                                                                </div>
                                                                <div className='head-btns'>
                                                                    <SVGIcons.ChatThemeCloseIcon />
                                                                </div>
                                                            </div>
                                                            <div className='message-box'>
                                                                <div className='user-msg'>
                                                                    <span>Hello, how are you doing?</span>
                                                                </div>
                                                                <div className='bot-msg'>
                                                                    {formData?.avatarImg ? <div className='avatar-preview'>
                                                                        <AvatarViewer className='chat-head' avatar={formData?.avatarImg} />
                                                                    </div> : null}
                                                                    <span>I'm doing well, thank you! How can I help <br />you today?</span>
                                                                </div>
                                                            </div>
                                                            <div className='chat-box'>
                                                                <img src={AppIcons.mobileThemeChatInput} alt='chat' />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </Col>
                                            </Row>}





                                        </Col>
                                    </Row> : null}
                                {/* -------------------------------------- end of page three ----------------------------------------- */}
                                {currentFormStep === 3 ?
                                    <Row gutter={90}>
                                        <Col span={24}>
                                            <br />
                                            <Title level={4} style={{ margin: '0', marginTop: '10px' }}>{translateThis('Module embedding URL')}</Title>
                                            <div className='module-url-wrp' style={{ marginBottom: '52px' }}>
                                                <span>{process.env.REACT_APP_AI_APP_URL}/{getCurrentFacilityURL()}/</span>

                                                <Form.Item
                                                    label={translateThis('Module URL')}
                                                    name="name"
                                                    rules={[{ required: true, message: `${translateThis('Please enter Module URL here')}` }]}
                                                    style={{ marginBottom: '52px' }}
                                                >
                                                    <Input required value={formData.name}
                                                        onChange={(e) => setFormValue('name', e.target.value)} placeholder={translateThis('Type here')} />
                                                </Form.Item>
                                                <span>{translateThis('/chat')}</span>
                                            </div>
                                        </Col>
                                        <Col span={24} >
                                            <div className='chat-window-ui preview'>
                                                <div className='chat-window'>
                                                    <Title level={4} style={{ textAlign: 'center' }}>{translateThis('Test')} {formData.name}  🤖</Title>
                                                    <div className="messaging">
                                                        <div className="inbox_msg">
                                                            <div className="messages">
                                                                <div className="msg_history" >

                                                                    <div style={{ padding: '10px' }} />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="chat-input module-chat">
                                                    <Form.Item
                                                    // initialValue={question}
                                                    >
                                                        <Input.TextArea
                                                            autoSize={{ minRows: 2, maxRows: 4 }}
                                                            // onKeyDown={(e) => {
                                                            //     if (e.key === 'Enter' && !e.shiftKey) {
                                                            //         e.preventDefault();
                                                            //         askAi(question);
                                                            //         setQuestion('');
                                                            //     }
                                                            // }} value={question}
                                                            // onChange={(e) => setQuestion(e.target.value)}
                                                            style={{ resize: 'none' }}
                                                            placeholder={translateThis('Enter a question and hit Enter.')} />

                                                    </Form.Item>

                                                    <Button type="primary"
                                                        // disabled={!question || isAsking}
                                                        // onClick={() => { askAi(question); setQuestion('') }}
                                                        className='sent-btn'
                                                        icon={<SVGIcons.SentIcon />} />

                                                </div>
                                            </div>

                                        </Col>

                                        <Col span={24} >
                                            <br />
                                            <br />
                                            <div className='maxWidth985px'>
                                                <Row gutter={40}>
                                                    <Col span={12}>
                                                        <div className='embed-code-box'>
                                                            <Title level={4} style={{ margin: '0 0 30px 0' }}>{translateThis('Create the')} <b>{translateThis('URL')}</b><br />{translateThis('for your site')}</Title>

                                                            <div className='embed-url-wrp'>
                                                                {/* <span>{process.env.REACT_APP_AI_APP_URL}/{getCurrentFacilityURL()}/</span> */}
                                                                <span style={{ width: '0', position: 'absolute', opacity: '0' }}>{moduleEmbeddedURL}</span>
                                                                <Button className='fill-btn create-btn' type="primary" title={translateThis('Copy URL')} shape="default" onClick={copyEmbeddingURL}>{translateThis('Copy URL')}</Button>

                                                                <div className='code-copy-field'>
                                                                    <Form.Item name="name" style={{ margin: '0px' }}>
                                                                        <Input value={formData.name} prefix={`${process.env.REACT_APP_AI_APP_URL}/${getCurrentFacilityURL()}/`} suffix={`/chat`}
                                                                            onChange={(e) => setFormValue('name', e.target.value)} placeholder={translateThis('Type here')} />
                                                                    </Form.Item>
                                                                    <Button className='fill-btn' type="primary" title={translateThis('Copy')} shape="default" onClick={copyEmbeddingURL}>{translateThis('Copy')}</Button>
                                                                </div>

                                                            </div>

                                                        </div>
                                                    </Col>
                                                    <Col span={12}>
                                                        <div className='embed-code-box'>
                                                            <Title level={4} style={{ margin: '0 0 30px 0' }}>{translateThis('Create the')} <b>{translateThis('<code />')}</b><br />{translateThis('for your site')}</Title>

                                                            <div className='embed-url-wrp'>

                                                                <Radio.Group
                                                                    value={widgetMode}
                                                                    onChange={(e) => setWidgetMode(e.target.value)}
                                                                    style={{ marginBottom: 12 }}
                                                                >
                                                                    <Radio value="bubble">{translateThis('Bubble')}</Radio>
                                                                    <Radio value="window">{translateThis('Window')}</Radio>
                                                                </Radio.Group>


                                                                <div className='code-copy-field'>
                                                                    <pre style={{
                                                                        width: '100%',
                                                                        margin: 0,
                                                                        padding: '8px 11px',
                                                                        fontFamily: 'monospace',
                                                                        fontSize: 12,
                                                                        background: '#f5f5f5',
                                                                        border: '1px solid #d9d9d9',
                                                                        borderRadius: 6,
                                                                        whiteSpace: 'pre-wrap',
                                                                        wordBreak: 'break-all',
                                                                        color: '#262626',
                                                                        minHeight: 100,
                                                                    }}>{formData.createdCode}</pre>
                                                                    <Button className='fill-btn' type="primary" title={translateThis('Copy')} shape="default" onClick={copyEmbedCodeSnippet} disabled={!formData.createdCode}>{translateThis('Copy')}</Button>
                                                                </div>

                                                            </div>

                                                        </div>
                                                    </Col>
                                                </Row>
                                            </div>

                                            <br />
                                            <br />
                                            <br />
                                            <br />
                                        </Col>
                                    </Row> : null}
                                {/* -------------------------------------- end of page four ----------------------------------------- */}
                            </Flex>
                        </div>

                        <div className='foot-btn-bar f-wrp'>
                            <div className="container">
                                <Flex gap={15} justify='space-between' align='center' horizontal="true" className='button-sec'>
                                    <Flex gap={15} style={{ width: '327px' }}>
                                        <Button className='cancel-btn' onClick={() => navigate(`/${getCurrentFacilityURL()}/ai-development/modules`, { replace: true })} type='default' htmlType="reset"> {translateThis("Back")} </Button>
                                    </Flex>

                                    <Flex gap={15}>
                                        <Button className='fill-btn arrow-button' disabled={currentFormStep === 0} onClick={() => {
                                            setCurrentFormStep(prev => Math.max(prev - 1, 0));
                                            scrollToTop.current?.scrollIntoView({ behavior: 'smooth' });
                                        }} shape="circle"><SVGIcons.ChevronLeftIcon /></Button>
                                        <Button className='fill-btn arrow-button' disabled={currentFormStep === totalSteps - 1} onClick={() => {
                                            setCurrentFormStep((prev) => Math.min(prev + 1, totalSteps - 1));
                                            scrollToTop.current?.scrollIntoView({ behavior: 'smooth' });
                                        }} shape="circle" ><SVGIcons.ChevronRightIcon /></Button>
                                    </Flex>

                                    <Flex gap={15}>
                                        <Button className='fill-btn save-btn' loading={isSubmitting} disabled={!isValidForm()} onClick={onSubmit} type="primary" htmlType="submit"> {id ? translateThis("Update") : translateThis("Save")} </Button>
                                        <Button className='fill-btn' loading={isSubmitting} disabled={!isValidForm()} onClick={onSubmit} type="primary" htmlType="submit"> {id ? translateThis("Update & Publish") : translateThis("Save & Publish")} </Button>
                                    </Flex>

                                </Flex>
                            </div>
                        </div>
                    </Form> : <span>{translateThis('Loading..')}</span>}
                </div>
            </div>
        </PageLayout >
    );
}
